import React, { useState, useEffect } from "react";
import AcessoRestrito from '../../components/AcessoRestrito';

// Material-UI Core Components
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Snackbar
} from "@mui/material";

// Material-UI Form Components
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Autocomplete
} from "@mui/material";

// Material-UI Table Components
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination
} from "@mui/material";

// Material-UI Layout Components
import {
  Grid
} from "@mui/material";

// Material-UI Dialog Components
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@mui/material";

// Material-UI Navigation Components
import {
  Tabs,
  Tab
} from "@mui/material";

// Material-UI Interactive Components
import {
  IconButton,
  Tooltip,
  Chip
} from "@mui/material";

// MUI X Date Pickers
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';

// Formatadores
import { converterStringParaDate, converterDateParaString, formatarDataParaExibicao } from '../../utils/formatadores';

// Material-UI Icons
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Edit as EditIcon, 
  Visibility as VisibilityIcon, 
  Close as CloseIcon, 
  BarChart as BarChartIcon,
  Block as BlockIcon,
  AttachMoney as AttachMoneyIcon
} from "@mui/icons-material";

// Importar ícones sem alias para compatibilidade
import { 
  Visibility, 
  Edit, 
  Delete, 
  Close, 
  Print, 
  Block
} from "@mui/icons-material";

// Recharts Components
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ComposedChart
} from 'recharts';

// Third-party libraries
import axios from "axios";
import api from "../../services/api";

// Utils
import { 
  formatarMoeda, 
  formatarMoedaInput,
  formatarCPFCNPJ, 
  formatarData, 
  removerFormatacao 
} from "../../utils/formatadores";
import { getHeaders } from "../../utils/apiUtils";
import env from '../../config/env.js';


// Array com as formas de pagamento disponíveis
const FORMAS_PAGAMENTO = [
  'Dinheiro',
  'PIX', 
  'Cartão Crédito',
  'Cartão Débito',
  'Boleto',
  'Transferência',
  'Cheque',
  'Outros'
];

const parseDataLocal = (valor) => {
  if (!valor) return null;
  if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? null : valor;
  const match = String(valor).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    const data = new Date(valor);
    return Number.isNaN(data.getTime()) ? null : data;
  }
  const [, ano, mes, dia] = match;
  const data = new Date(Number(ano), Number(mes) - 1, Number(dia), 12);
  return data.getFullYear() === Number(ano) &&
    data.getMonth() === Number(mes) - 1 &&
    data.getDate() === Number(dia)
    ? data
    : null;
};

// Função utilitária para converter número para extenso
const numeroParaExtenso = (numero) => {
  const n = Number(numero) | 0;
  const unidades = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
  const dezenas = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
  const especiais = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
  const centenas = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
  
  if (n === 0) return 'zero';
  if (n < 10) return unidades[n];
  if (n < 20) return especiais[n - 10];
  if (n < 100) {
    const unidade = n % 10;
    const dezena = Math.floor(n / 10);
    return dezenas[dezena] + (unidade ? ' e ' + unidades[unidade] : '');
  }
  if (n === 100) return 'cem';
  if (n < 1000) {
    const centena = Math.floor(n / 100);
    const resto = n % 100;
    return centenas[centena] + (resto ? ' e ' + numeroParaExtenso(resto) : '');
  }
  if (n < 1000000) {
    const milhar = Math.floor(n / 1000);
    const resto = n % 1000;
    return (milhar === 1 ? 'mil' : numeroParaExtenso(milhar) + ' mil') + (resto ? ' ' + numeroParaExtenso(resto) : '');
  }
  return n.toString();
};

// Função utilitária para decodificar JWT
const decodeJwtPayload = (token) => {
  if (typeof token !== 'string' || token.split('.').length < 2) {
    throw new Error('Token JWT inválido: formato inesperado.');
  }
  const payloadB64 = token.split('.')[1]
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(token.split('.')[1].length / 4) * 4, '=');

  const json = (typeof atob === 'function')
    ? atob(payloadB64)
    : Buffer.from(payloadB64, 'base64').toString('utf8');

  return JSON.parse(json);
};

// Funções utilitárias importadas de apiUtils.js

export default function Financeiro() {
  // Estados para mensagens
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Função para criar logs de auditoria
  // Função de log de auditoria removida - agora é feita no backend
  const [orderBy, setOrderBy] = useState("data_emissao");
  const [order, setOrder] = useState("desc");
  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [lancamentoSelecionado, setLancamentoSelecionado] = useState(null);
  const [lancamentoVisualizar, setLancamentoVisualizar] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [tipoPessoa, setTipoPessoa] = useState("cliente"); // "cliente" ou "colaborador"
  const [tipoDocumento, setTipoDocumento] = useState("cpf"); // "cpf" ou "cnpj"
  
  // Estados para o gráfico
  const [dataInicioGrafico, setDataInicioGrafico] = useState("");
  const [dataFimGrafico, setDataFimGrafico] = useState("");
  const [tipoGrafico, setTipoGrafico] = useState("barra"); // "barra", "linha", "pizza"
  const [dadosGrafico, setDadosGrafico] = useState([]);
  
  // Estados para Relatórios Gerenciais
  const [openRelatorios, setOpenRelatorios] = useState(false);
  const [relatorioSelecionado, setRelatorioSelecionado] = useState(null);
  const [dadosRelatorio, setDadosRelatorio] = useState({});
  const [loadingRelatorio, setLoadingRelatorio] = useState(false);
  const [filtrosRelatorio, setFiltrosRelatorio] = useState(() => {
    const now = new Date();
    const inicio = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fim = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    return {
      dataInicio: inicio,
      dataFim: fim,
      categoria: '',
      tipo: '',
      status: ''
    };
  });
  
  // Estados para filtros específicos da inadimplência
  const [filtrosInadimplencia, setFiltrosInadimplencia] = useState({
    diasAtrasoMin: '',
    diasAtrasoMax: '',
    valorMin: '',
    valorMax: '',
    categoria: '',
    cliente: ''
  });
  
  // Estados para organização
  const [formData, setFormData] = useState({
    tipo: "receber",
    categoria: "",
    descricao: "",
    id_pessoa: "",
    nome_pessoa: "",
    documento_pessoa: "",
    valor_total: "",
    forma_pagamento: "",
    data_emissao: "",
    data_vencimento: "",
    status: "pendente",
    data_pagamento: "",
    valor_pago: "",
    observacoes: "",
    anexo: ""
  });

  // Estados adicionais que estavam faltando
  const [loading, setLoading] = useState(false);
  const [lancamentos, setLancamentos] = useState([]);
  const [lancamentosCompletos, setLancamentosCompletos] = useState([]);
  const [totalLancamentos, setTotalLancamentos] = useState(0);
  const [categorias, setCategorias] = useState([]);
  const [loadingCategorias, setLoadingCategorias] = useState(true);
  const [erroCategorias, setErroCategorias] = useState('');
  const [clientes, setClientes] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [filtros, setFiltros] = useState({
    descricao: '',
    cliente: '',
    categoria: '',
    tipo: '',
    status: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formError, setFormError] = useState({});
  const [tentouSalvar, setTentouSalvar] = useState(false);

  const st_codHashEmpresa = localStorage.getItem("st_codHashEmpresa");

  // ========================================
  // FUNÇÕES DE CARREGAMENTO DE DADOS
  // ========================================

  const carregarLancamentos = async () => {
    setLoading(true);
    try {
      const paramsBase = {
          page: page + 1,
          limit: rowsPerPage,
          descricao: filtros.descricao || undefined,
          cliente: filtros.cliente || undefined,
          categoria: filtros.categoria || undefined,
          tipo: filtros.tipo || undefined,
          status: filtros.status || undefined,
          orderBy,
          order
      };
      const paramsCompletos = { ...paramsBase };
      delete paramsCompletos.page;
      delete paramsCompletos.limit;
      const [res, resCompleto] = await Promise.all([
        api.get('/financeiro', { params: paramsBase }),
        api.get('/financeiro', { params: paramsCompletos })
      ]);
      
      let lista = [];
      if (Array.isArray(res.data?.dados)) {
        lista = res.data.dados;
        setTotalLancamentos(Number(res.data.total) || 0);
      } else if (Array.isArray(res.data)) {
        // Compatibilidade temporária com uma API ainda não atualizada.
        lista = res.data;
        setTotalLancamentos(lista.length);
      }
      
      setLancamentos(lista);
      setLancamentosCompletos(Array.isArray(resCompleto.data) ? resCompleto.data : []);
      
    } catch (error) {
      console.error('Erro ao carregar lançamentos:', error);
      setError('Erro ao carregar lançamentos');
    } finally {
      setLoading(false);
    }
  };

  const carregarCategorias = async () => {
    setLoadingCategorias(true);
    setErroCategorias('');
    try {
      const codHashEmpresa = localStorage.getItem('st_codHashEmpresa');
      const response = await api.get(`/categoria/empresa/${encodeURIComponent(codHashEmpresa || 'empresa')}`);
      const lista = Array.isArray(response.data) ? response.data : [];
      setCategorias(
        lista
          .filter(categoria => categoria.bo_ativo !== false)
          .sort((a, b) => String(a.st_nomeCategoria || '').localeCompare(
            String(b.st_nomeCategoria || ''),
            'pt-BR'
          ))
      );
    } catch (error) {
      console.error('Erro ao carregar categorias financeiras:', error);
      setCategorias([]);
      setErroCategorias(
        error.response?.data?.error ||
        'Não foi possível carregar as categorias.'
      );
    } finally {
      setLoadingCategorias(false);
    }
  };

  const carregarClientes = async () => {
    try {
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const response = await api.get(`/clientes?st_codHashEmpresa=${codHashEmpresa}`);
      if (response.data && Array.isArray(response.data)) {
        setClientes(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const carregarColaboradores = async () => {
    try {
      const codHashEmpresa = localStorage.getItem("st_codHashEmpresa");
      const response = await api.get(`/colaboradores?st_codHashEmpresa=${codHashEmpresa}`);
      if (response.data && Array.isArray(response.data)) {
        setColaboradores(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error);
    }
  };

  useEffect(() => {
    carregarClientes();
    carregarColaboradores();
    carregarCategorias();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      carregarLancamentos();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [page, rowsPerPage, filtros, orderBy, order]);

  // Função para gerar dados do gráfico baseado nos lançamentos
  const gerarDadosGrafico = () => {
    if (!lancamentosCompletos || lancamentosCompletos.length === 0) {
      setDadosGrafico([]);
      return;
    }

    const hoje = new Date();
    const dadosGrafico = [];
    
    // Obter todas as categorias únicas
    const categorias = [...new Set(lancamentosCompletos.map(l => l.categoria).filter(Boolean))];
    
    // Gerar dados dos últimos 6 meses
    for (let i = 5; i >= 0; i--) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
      const mes = data.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      
      // Filtrar lançamentos do mês
      const lancamentosMes = lancamentosCompletos.filter(lancamento => {
        const dataLancamento = parseDataLocal(lancamento.data_emissao);
        if (!dataLancamento || lancamento.status === 'cancelado') return false;
        return dataLancamento.getFullYear() === data.getFullYear() && 
               dataLancamento.getMonth() === data.getMonth();
      });
      
      // Criar objeto para o mês com dados por categoria
      const dadosMes = { mes };
      
      // Calcular totais por categoria
      categorias.forEach(categoria => {
        const lancamentosCategoria = lancamentosMes.filter(l => l.categoria === categoria);
        
        const receberCategoria = lancamentosCategoria
          .filter(l => l.tipo === 'receber')
          .reduce((sum, l) => sum + parseFloat(l.valor_total || 0), 0);
        
        const pagarCategoria = lancamentosCategoria
          .filter(l => l.tipo === 'pagar')
          .reduce((sum, l) => sum + parseFloat(l.valor_total || 0), 0);
        
        // Adicionar dados da categoria ao mês
        dadosMes[`${categoria}_receber`] = receberCategoria;
        dadosMes[`${categoria}_pagar`] = pagarCategoria;
        dadosMes[`${categoria}_saldo`] = receberCategoria - pagarCategoria;
      });
      
      // Calcular totais gerais do mês
      const receberTotal = lancamentosMes
        .filter(l => l.tipo === 'receber')
        .reduce((sum, l) => sum + parseFloat(l.valor_total || 0), 0);
      
      const pagarTotal = lancamentosMes
        .filter(l => l.tipo === 'pagar')
        .reduce((sum, l) => sum + parseFloat(l.valor_total || 0), 0);
      
      dadosMes.receber_total = receberTotal;
      dadosMes.pagar_total = pagarTotal;
      dadosMes.saldo_total = receberTotal - pagarTotal;
      
      dadosGrafico.push(dadosMes);
    }
    
    setDadosGrafico(dadosGrafico);
  };

  // useEffect para gerar dados do gráfico quando os lançamentos mudarem
  useEffect(() => {
    gerarDadosGrafico();
  }, [lancamentosCompletos]);

  // ========================================
  // FUNÇÕES UTILITÁRIAS
  // ========================================

  // Função para obter data e hora atual formatada
  const dataHoraAtual = () => {
    const agora = new Date();
    return agora.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Função para converter data para formato de input
  const converterDataParaInput = (data) => {
    if (!data) return '';
    const dataObj = new Date(data);
    return dataObj.toISOString().split('T')[0];
  };

  // Função para imprimir comprovante
  const imprimirComprovante = (lancamento) => {
    const printWindow = window.open('', '_blank');
    const dataHora = dataHoraAtual();
    
    // Recupera nome da empresa do token JWT
    let nomeEmpresa = '';
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payloadB64 = token.split('.')[1]
          .replace(/-/g, '+')
          .replace(/_/g, '/')
          .padEnd(Math.ceil(token.split('.')[1].length / 4) * 4, '=');
        
        const json = atob(payloadB64);
        const payload = JSON.parse(json);
        nomeEmpresa = (payload && (payload.nomeempresa || payload.nomeEmpresa)) || 'Empresa';
      }
    } catch (err) {
      nomeEmpresa = 'Empresa';
    }
    
    // Determina o tipo de documento
    const tipoDocumento = lancamento.tipo === 'receber' ? 'RECEBIMENTO' : 'PAGAMENTO';
    const tipoOperacao = lancamento.tipo === 'receber' ? 'Recebido de' : 'Pago para';
    
    // Formata valor por extenso
    const formatarValorExtenso = (valor) => {
      const numero = parseFloat(valor);
      const inteiro = Math.floor(numero);
      const decimal = Math.round((numero - inteiro) * 100);
      
      const valorExtenso = numeroParaExtenso(inteiro);
      const centavosExtenso = decimal > 0 ? numeroParaExtenso(decimal) : '';
      
      return `${valorExtenso} real${inteiro !== 1 ? 'es' : ''}${decimal > 0 ? ' e ' + centavosExtenso + ' centavo' + (decimal !== 1 ? 's' : '') : ''}`;
    };
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <title>Comprovante de ${tipoDocumento}</title>
        <style>
          @page {
            size: A4;
            margin: 2cm;
          }
          body { 
            font-family: 'Times New Roman', serif; 
            margin: 0; 
            padding: 0;
            font-size: 12px;
            line-height: 1.4;
          }
          .container {
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            border-bottom: 2px solid #000;
            padding-bottom: 20px;
          }
          .empresa-nome {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 10px;
            text-transform: uppercase;
          }
          .documento-titulo {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 20px;
            text-transform: uppercase;
          }
          .content {
            margin-bottom: 40px;
          }
          .info-row {
            display: flex;
            margin-bottom: 15px;
            align-items: flex-start;
          }
          .info-label {
            font-weight: bold;
            min-width: 120px;
            margin-right: 10px;
          }
          .info-value {
            flex: 1;
            border-bottom: 1px solid #000;
            padding-bottom: 2px;
            min-height: 18px;
          }
          .valor-destaque {
            font-size: 14px;
            font-weight: bold;
            text-align: center;
            margin: 20px 0;
            padding: 15px;
            border: 2px solid #000;
            background-color: #f9f9f9;
          }
          .valor-extenso {
            font-style: italic;
            margin-top: 10px;
            text-align: center;
          }
          .assinaturas {
            margin-top: 60px;
            display: flex;
            justify-content: space-between;
          }
          .assinatura-box {
            text-align: center;
            width: 45%;
          }
          .assinatura-linha {
            border-bottom: 1px solid #000;
            height: 40px;
            margin-bottom: 5px;
          }
          .assinatura-label {
            font-size: 10px;
            font-weight: bold;
          }
          .data-local {
            margin-top: 30px;
            text-align: right;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #ccc;
            padding-top: 10px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="empresa-nome">${nomeEmpresa}</div>
            <div class="documento-titulo">COMPROVANTE DE ${tipoDocumento}</div>
          </div>
          
          <div class="content">
            <div class="info-row">
              <div class="info-label">${tipoOperacao}:</div>
              <div class="info-value">${lancamento.nome_pessoa || 'N/A'}</div>
            </div>
            
            <div class="info-row">
              <div class="info-label">Documento:</div>
              <div class="info-value">${lancamento.documento_pessoa || 'N/A'}</div>
            </div>
            
            <div class="info-row">
              <div class="info-label">Descrição:</div>
              <div class="info-value">${lancamento.descricao || 'N/A'}</div>
            </div>
            
            <div class="info-row">
              <div class="info-label">Categoria:</div>
              <div class="info-value">${lancamento.categoria || 'N/A'}</div>
            </div>
            
            <div class="info-row">
              <div class="info-label">Forma de Pagamento:</div>
              <div class="info-value">${lancamento.forma_pagamento || 'N/A'}</div>
            </div>
            
            <div class="info-row">
              <div class="info-label">Data de Emissão:</div>
              <div class="info-value">${lancamento.data_emissao ? new Date(lancamento.data_emissao).toLocaleDateString('pt-BR') : 'N/A'}</div>
            </div>
            
            <div class="info-row">
              <div class="info-label">Data de Vencimento:</div>
              <div class="info-value">${lancamento.data_vencimento ? new Date(lancamento.data_vencimento).toLocaleDateString('pt-BR') : 'N/A'}</div>
            </div>
            
            ${lancamento.data_pagamento ? `
            <div class="info-row">
              <div class="info-label">Data de ${tipoDocumento}:</div>
              <div class="info-value">${new Date(lancamento.data_pagamento).toLocaleDateString('pt-BR')}</div>
            </div>
            ` : ''}
            
            <div class="valor-destaque">
              VALOR: R$ ${formatarMoeda(lancamento.valor_total || 0)}
              <div class="valor-extenso">
                (${formatarValorExtenso(lancamento.valor_total || 0)})
              </div>
            </div>
            
            ${lancamento.observacoes ? `
            <div class="info-row">
              <div class="info-label">Observações:</div>
              <div class="info-value">${lancamento.observacoes}</div>
            </div>
            ` : ''}
          </div>
          
          <div class="assinaturas">
            <div class="assinatura-box">
              <div class="assinatura-linha"></div>
              <div class="assinatura-label">ASSINATURA DO ${lancamento.tipo === 'receber' ? 'RECEBEDOR' : 'PAGADOR'}</div>
            </div>
            <div class="assinatura-box">
              <div class="assinatura-linha"></div>
              <div class="assinatura-label">ASSINATURA DO RESPONSÁVEL</div>
            </div>
          </div>
          
          <div class="data-local">
            <div class="info-row">
              <div class="info-label">Data:</div>
              <div class="info-value" style="min-width: 100px;">${new Date().toLocaleDateString('pt-BR')}</div>
            </div>
            <div class="info-row">
              <div class="info-label">Local:</div>
              <div class="info-value" style="min-width: 200px;">_________________________</div>
            </div>
          </div>
          
          <div class="footer">
            <p>Documento gerado automaticamente em ${dataHora}</p>
            <p>Este comprovante é válido para fins de comprovação de ${lancamento.tipo === 'receber' ? 'recebimento' : 'pagamento'}</p>
          </div>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Função para fechar relatórios
  const fecharRelatorios = () => {
    setOpenRelatorios(false);
    setRelatorioSelecionado(null);
    setDadosRelatorio({});
  };

  // ========================================
  // HANDLERS DE FORMULÁRIO
  // ========================================
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    // Formatação específica para documento baseada no tipo
    if (name === "documento_pessoa") {
      if (tipoPessoa === "cliente") {
        newValue = formatarCPFCNPJ(value);
      } else if (tipoPessoa === "colaborador") {
        if (tipoDocumento === "cpf") {
          newValue = formatarCPFCNPJ(value);
        } else if (tipoDocumento === "cnpj") {
          newValue = formatarCPFCNPJ(value);
        }
      }
    }

    setFormData({ ...formData, [name]: newValue });
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    
    // Preenchimento automático do documento ao selecionar pessoa
    if (name === "nome_pessoa" && value) {
      if (tipoPessoa === "cliente") {
        const clienteSelecionado = clientes.find(cliente => 
          cliente.nome_completo === value
        );
        if (clienteSelecionado) {
          // Para clientes, sempre usa o campo cpf_cnpj
          setFormData(prev => ({
            ...prev,
            id_pessoa: (clienteSelecionado.id && clienteSelecionado.id.toString()) || "",
            documento_pessoa: clienteSelecionado.cpf_cnpj || ""
          }));
        }
      } else if (tipoPessoa === "colaborador") {
        const colaboradorSelecionado = colaboradores.find(colaborador => 
          colaborador.nome_completo === value
        );
        if (colaboradorSelecionado) {
          let documento = "";
          if (tipoDocumento === "cpf") {
            documento = colaboradorSelecionado.cpf || "";
          } else if (tipoDocumento === "cnpj" && colaboradorSelecionado.cnpj) {
            documento = colaboradorSelecionado.cnpj || "";
          }
          
          setFormData(prev => ({
            ...prev,
            id_pessoa: (colaboradorSelecionado.id && colaboradorSelecionado.id.toString()) || "",
            documento_pessoa: documento
          }));
        }
      }
    }
  };

  const handleTipoPessoaChange = (event) => {
    const novoTipo = event.target.value;
    setTipoPessoa(novoTipo);
    
    // Limpa os campos relacionados quando muda o tipo
    setFormData(prev => ({
      ...prev,
      nome_pessoa: "",
      id_pessoa: "",
      documento_pessoa: ""
    }));
    
    // Reseta o tipo de documento para CPF quando muda para colaborador
    if (novoTipo === "colaborador") {
      setTipoDocumento("cpf");
    }
  };

  const handleTipoDocumentoChange = (event) => {
    const novoTipo = event.target.value;
    setTipoDocumento(novoTipo);
    
    // Atualiza o documento apenas para colaboradores
    if (formData.nome_pessoa && tipoPessoa === "colaborador") {
      const colaboradorSelecionado = colaboradores.find(colaborador => 
        colaborador.nome_completo === formData.nome_pessoa
      );
      if (colaboradorSelecionado) {
        let documento = "";
        if (novoTipo === "cpf") {
          documento = colaboradorSelecionado.cpf || "";
        } else if (novoTipo === "cnpj" && colaboradorSelecionado.cnpj) {
          documento = colaboradorSelecionado.cnpj || "";
        }
        
        setFormData(prev => ({
          ...prev,
          documento_pessoa: documento
        }));
      }
    }
  };

  // ========================================
  // FUNÇÕES DE FORMATAÇÃO
  // ========================================
  
  const handleValorChange = (name, value) => {
    // Remove formatação para armazenar apenas números
    removerFormatacao(value);
    
    // Aplica formatação monetária em tempo real
    const valorFormatado = formatarMoedaInput(value);
    
    // Atualiza o formData com o valor formatado para exibição
    setFormData({ ...formData, [name]: valorFormatado });
  };

  // Função para converter data do formato DD/MM/AAAA para YYYY-MM-DD (para o input type="date")
  const formatarDataParaInput = (data) => {
    if (!data) return '';
    
    // Se já estiver no formato YYYY-MM-DD, retorna como está
    if (data.includes('-')) return data;
    
    // Remove caracteres não numéricos
    const numeros = data.replace(/\D/g, '');
    
    // Se tiver 8 dígitos, assume formato DDMMAAAA
    if (numeros.length === 8) {
      const dia = numeros.slice(0, 2);
      const mes = numeros.slice(2, 4);
      const ano = numeros.slice(4, 8);
      return `${ano}-${mes}-${dia}`;
    }
    
    // Se tiver 6 dígitos, assume formato DDMMAA
    if (numeros.length === 6) {
      const dia = numeros.slice(0, 2);
      const mes = numeros.slice(2, 4);
      const ano = '20' + numeros.slice(4, 6);
      return `${ano}-${mes}-${dia}`;
    }
    
    return data;
  };

  // Função para converter data do formato YYYY-MM-DD para DD/MM/AAAA (para exibição)
  const formatarDataParaExibicao = (data) => {
    if (!data) return '';
    
    // Se já estiver no formato DD/MM/AAAA, retorna como está
    if (data.includes('/')) return data;
    
    // Se estiver no formato YYYY-MM-DD
    if (data.includes('-')) {
      const [ano, mes, dia] = data.split('-');
      return `${dia}/${mes}/${ano}`;
    }
    
    return data;
  };

  // Função para lidar com mudanças em campos de data
  const handleDataChange = (name, value) => {
    // Se o valor estiver vazio, limpa o campo
    if (!value) {
      setFormData(prev => name === 'data_emissao'
        ? {
            ...prev,
            data_emissao: '',
            data_vencimento: '',
            data_pagamento: ''
          }
        : { ...prev, [name]: '' }
      );
      return;
    }
    
    // Remove caracteres não numéricos
    const numeros = value.replace(/\D/g, '');
    
    // Se tiver mais de 8 dígitos, limita a 8
    if (numeros.length > 8) {
      return;
    }
    
    // Formata como DD/MM/AAAA
    let dataFormatada = '';
    if (numeros.length >= 1) dataFormatada += numeros.slice(0, 1);
    if (numeros.length >= 2) dataFormatada += numeros.slice(1, 2) + '/';
    if (numeros.length >= 3) dataFormatada += numeros.slice(2, 3);
    if (numeros.length >= 4) dataFormatada += numeros.slice(3, 4) + '/';
    if (numeros.length >= 5) dataFormatada += numeros.slice(4, 8);
    
    setFormData(prev => name === 'data_emissao'
      ? {
          ...prev,
          data_emissao: dataFormatada,
          data_vencimento: dataFormatada,
          data_pagamento: dataFormatada
        }
      : { ...prev, [name]: dataFormatada }
    );
  };

  // Função para lidar com mudanças em campos de data do gráfico
  const handleDataChangeGrafico = (tipo, value) => {
    // Se o valor estiver vazio, limpa o campo
    if (!value) {
      if (tipo === "dataInicio") {
        setDataInicioGrafico('');
      } else if (tipo === "dataFim") {
        setDataFimGrafico('');
      }
      return;
    }
    
    // Remove caracteres não numéricos
    const numeros = value.replace(/\D/g, '');
    
    // Se tiver mais de 8 dígitos, limita a 8
    if (numeros.length > 8) {
      return;
    }
    
    // Formata como DD/MM/AAAA
    let dataFormatada = '';
    if (numeros.length >= 1) dataFormatada += numeros.slice(0, 1);
    if (numeros.length >= 2) dataFormatada += numeros.slice(1, 2) + '/';
    if (numeros.length >= 3) dataFormatada += numeros.slice(2, 3);
    if (numeros.length >= 4) dataFormatada += numeros.slice(3, 4) + '/';
    if (numeros.length >= 5) dataFormatada += numeros.slice(4, 8);
    
    // Converte para formato YYYY-MM-DD para armazenamento
    const dataConvertida = formatarDataParaInput(dataFormatada);
    
    if (tipo === "dataInicio") {
      setDataInicioGrafico(dataConvertida);
    } else if (tipo === "dataFim") {
      setDataFimGrafico(dataConvertida);
    }
  };

  // ========================================
  // FUNÇÕES DO GRÁFICO
  // ========================================
  
  // Função para processar dados do gráfico
  const processarDadosGrafico = () => {
    if (!lancamentosCompletos.length) return [];
    
    // Filtra lançamentos por período se especificado
    let lancamentosFiltrados = lancamentosCompletos.filter((l) => l.status !== 'cancelado');
    
    if (dataInicioGrafico && dataFimGrafico) {
      const inicio = parseDataLocal(dataInicioGrafico);
      const fim = parseDataLocal(dataFimGrafico);
      
      lancamentosFiltrados = lancamentosFiltrados.filter(lanc => {
        const dataLanc = parseDataLocal(lanc.data_emissao);
        if (!dataLanc) return false;
        return dataLanc >= inicio && dataLanc <= fim;
      });
    }
    
    // Agrupa por categoria e tipo
    const dadosPorCategoria = {};
    
    lancamentosFiltrados.forEach(lanc => {
      const categoria = lanc.categoria || 'Sem Categoria';
      const tipo = lanc.tipo || 'receber';
      const valor = parseFloat(lanc.valor_total) || 0;
      
      if (!dadosPorCategoria[categoria]) {
        dadosPorCategoria[categoria] = {
          categoria,
          receber: 0,
          pagar: 0,
          total: 0
        };
      }
      
      dadosPorCategoria[categoria][tipo] += valor;
      dadosPorCategoria[categoria].total += valor;
    });
    
    // Converte para array e ordena por total
    const dadosProcessados = Object.values(dadosPorCategoria)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10); // Limita a 10 categorias para melhor visualização
    
    return dadosProcessados;
  };
  
  // Função para gerar dados por mês
  const processarDadosPorMes = () => {
    if (!lancamentosCompletos.length) return [];
    
    // Filtra por período se especificado
    let lancamentosFiltrados = lancamentosCompletos.filter((l) => l.status !== 'cancelado');
    
    if (dataInicioGrafico && dataFimGrafico) {
      const inicio = parseDataLocal(dataInicioGrafico);
      const fim = parseDataLocal(dataFimGrafico);
      
      lancamentosFiltrados = lancamentosFiltrados.filter(lanc => {
        const dataLanc = parseDataLocal(lanc.data_emissao);
        if (!dataLanc) return false;
        return dataLanc >= inicio && dataLanc <= fim;
      });
    }
    
    // Agrupa por mês
    const dadosPorMes = {};
    
    lancamentosFiltrados.forEach(lanc => {
      const data = parseDataLocal(lanc.data_emissao);
      if (!data) return;
      const mesAno = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
      const tipo = lanc.tipo || 'receber';
      const valor = parseFloat(lanc.valor_total) || 0;
      
      if (!dadosPorMes[mesAno]) {
        dadosPorMes[mesAno] = {
          mes: mesAno,
          receber: 0,
          pagar: 0,
          total: 0
        };
      }
      
      dadosPorMes[mesAno][tipo] += valor;
      dadosPorMes[mesAno].total += valor;
    });
    
    // Converte para array e ordena por mês
    return Object.values(dadosPorMes).sort((a, b) => a.mes.localeCompare(b.mes));
  };
  

  // Função para imprimir o gráfico
  const imprimirGrafico = () => {
    // Recupera nome da empresa do token JWT
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const nomeEmpresa = (payload && payload.nomeempresa) || '';
      }
    } catch (error) {
      console.error('Erro ao obter nome da empresa:', error);
    }
    // Log de auditoria removido - agora é feito no backend

    // Gera dados para o relatório
    const dadosRelatorio = tipoGrafico === 'pizza' ? processarDadosGrafico() : processarDadosPorMes();
    const totalReceitas = dadosRelatorio.reduce((total, item) => total + (item.receber || 0), 0);
    const totalDespesas = dadosRelatorio.reduce((total, item) => total + (item.pagar || 0), 0);
    const saldo = totalReceitas - totalDespesas;

    // Filtros aplicados
    const filtrosAplicados = [];
    if (dataInicioGrafico) filtrosAplicados.push(`Data Início: ${formatarDataParaExibicao(dataInicioGrafico)}`);
    if (dataFimGrafico) filtrosAplicados.push(`Data Fim: ${formatarDataParaExibicao(dataFimGrafico)}`);

    const conteudo = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Relatório Gráfico Financeiro</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            font-size: 12px;
            line-height: 1.4;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
            margin-bottom: 20px;
          }
          .title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .subtitle {
            font-size: 14px;
            color: #666;
          }
          .info-section {
            margin-bottom: 15px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
          }
          .label {
            font-weight: bold;
            min-width: 120px;
          }
          .value {
            flex: 1;
          }
          .resumo {
            display: flex;
            justify-content: space-around;
            margin: 20px 0;
            flex-wrap: wrap;
          }
          .card-resumo {
            background-color: #f5f5f5;
            padding: 15px;
            border-radius: 8px;
            text-align: center;
            min-width: 150px;
            margin: 10px;
            border: 1px solid #ddd;
          }
          .card-resumo h3 {
            color: #1976d2;
            margin: 0 0 10px 0;
            font-size: 16px;
          }
          .receitas { color: #4caf50; }
          .despesas { color: #f44336; }
          .saldo { color: #1976d2; }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
          }
          th, td {
            padding: 8px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          th {
            background-color: #e8e8e8;
            color: #1976d2;
            font-weight: bold;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #ccc;
            padding-top: 10px;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">RELATÓRIO GRÁFICO FINANCEIRO</div>
          <div class="subtitle">Sistema de Gestão Financeira</div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="label">Tipo de Gráfico:</span>
            <span class="value">${tipoGrafico === 'barra' ? 'Gráfico de Barras' : tipoGrafico === 'linha' ? 'Gráfico de Linhas' : 'Gráfico de Pizza'}</span>
          </div>
          <div class="info-row">
            <span class="label">Período:</span>
            <span class="value">${filtrosAplicados.length > 0 ? filtrosAplicados.join(' | ') : 'Todos os períodos'}</span>
          </div>
          <div class="info-row">
            <span class="label">Data de Geração:</span>
            <span class="value">${dataHoraAtual}</span>
          </div>
        </div>

        <div class="resumo">
          <div class="card-resumo">
            <h3 class="receitas">Total de Receitas</h3>
            <div class="receitas" style="font-size: 20px; font-weight: bold;">
              ${formatarMoeda(totalReceitas)}
            </div>
          </div>
          <div class="card-resumo">
            <h3 class="despesas">Total de Despesas</h3>
            <div class="despesas" style="font-size: 20px; font-weight: bold;">
              ${formatarMoeda(totalDespesas)}
            </div>
          </div>
          <div class="card-resumo">
            <h3 class="saldo">Saldo</h3>
            <div class="saldo" style="font-size: 20px; font-weight: bold;">
              ${formatarMoeda(saldo)}
            </div>
          </div>
        </div>

        <div class="info-section">
          <h3 style="color: #1976d2; margin-bottom: 15px;">Dados Detalhados</h3>
          <table>
            <thead>
              <tr>
                <th>${tipoGrafico === 'pizza' ? 'Categoria' : 'Mês'}</th>
                <th>Receitas</th>
                <th>Despesas</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${dadosRelatorio.map(item => `
                <tr>
                  <td>${tipoGrafico === 'pizza' ? item.categoria : item.mes}</td>
                  <td style="color: #4caf50;">${formatarMoeda(item.receber || 0)}</td>
                  <td style="color: #f44336;">${formatarMoeda(item.pagar || 0)}</td>
                  <td style="font-weight: bold;">${formatarMoeda(item.total || 0)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="footer">
          <div>Relatório gerado em: ${dataHoraAtual}</div>
          <div>Empresa: N/A</div>
          <div>Sistema de Gestão Financeira - Todos os direitos reservados</div>
        </div>
      </body>
      </html>
    `;

    const janelaGrafico = window.open('', '_blank', 'width=800,height=600');
    janelaGrafico.document.write(conteudo);
    janelaGrafico.document.close();
    
    // Aguarda um pouco para o conteúdo carregar antes de imprimir
    setTimeout(() => {
      janelaGrafico.print();
      janelaGrafico.close();
    }, 500);

    // Log de auditoria removido - agora é feito no backend
  };
  
  // Função para imprimir comprovante
  // ========================================
  // FUNÇÕES DE UTILIDADE
  // ========================================
  
  const imprimirRecibo = () => {
    // Recupera nome da empresa do token JWT (seguro e cross-env)
    let nomeEmpresa = '';


    try {
      const token = (typeof localStorage !== 'undefined')
        ? localStorage.getItem('token')     // browser
        : process.env.TOKEN;                // fallback em Node (ajuste conforme o seu caso)

      if (token) {
        const payload = decodeJwtPayload(token);
        nomeEmpresa = (payload && (payload.nomeempresa || payload.nomeEmpresa)) || '';
      }
      } catch (err) {
        // trate como achar melhor: log, telemetria, etc.
        console.error('Falha ao decodificar token JWT:', err);
      }

    // ----------------- A PARTIR DAQUI SEGUE O RESTO DA SUA LÓGICA -----------------

    // Log de auditoria removido - agora é feito no backend

    // Calcula totais dos registros filtrados
    const totalReceber = (lancamentosFiltrados || [])
      .filter(l => l.tipo === 'receber')
      .reduce((sum, l) => sum + parseFloat(l.valor_total || 0), 0);
    
    const totalPagar = (lancamentosFiltrados || [])
      .filter(l => l.tipo === 'pagar')
      .reduce((sum, l) => sum + parseFloat(l.valor_total || 0), 0);
    
    const saldo = totalReceber - totalPagar;
    
    
    // Filtros aplicados
    const filtrosAplicados = [];
    if (filtros?.descricao) filtrosAplicados.push(`Descrição: ${filtros.descricao}`);
    if (filtros?.cliente)   filtrosAplicados.push(`Nome: ${filtros.cliente}`);
    if (filtros?.categoria) filtrosAplicados.push(`Categoria: ${filtros.categoria}`);
    if (filtros?.tipo)      filtrosAplicados.push(`Tipo: ${filtros.tipo}`);
    if (filtros?.status)    filtrosAplicados.push(`Status: ${filtros.status}`);



    const gerarTabelaItens = () => {
      if (!Array.isArray(lancamentosFiltrados) || lancamentosFiltrados.length === 0) return '';
      
      let html = `
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <thead>
            <tr style="background-color: #f5f5f5;">
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Item</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Descrição</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Categoria</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: center;">Tipo</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Valor (R$)</th>
            </tr>
          </thead>
          <tbody>
      `;
      
      lancamentosFiltrados.forEach((item, index) => {
        html += `
          <tr>
            <td style="border: 1px solid #ddd; padding: 8px;">${index + 1}</td>
            <td style="border: 1px solid #ddd; padding: 8px;">${item?.descricao || 'N/A'}</td>
            <td style="border: 1px solid #ddd; padding: 8px;">${item?.categoria || 'N/A'}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${item?.tipo === 'receber' ? 'Receber' : 'Pagar'}</td>
            <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${formatarMoeda?.(item?.valor_total || 0) || (item?.valor_total || 0)}</td>
          </tr>
        `;
      });
      
      html += `
          </tbody>
        </table>
        <div style="text-align: right; margin-top: 10px; font-weight: bold; font-size: 16px;">
          Total Geral: ${formatarMoeda?.(Math.abs(saldo)) || Math.abs(saldo)}
        </div>
      `;
      
      return html;
    };

    // ... use dataAtual, tipoOperacao, filtrosAplicados, valorExtenso, gerarTabelaItens, etc.
  };

  const abrirFormulario = (lancamento = null) => {
    setLancamentoSelecionado(lancamento);
    if (lancamento) {
      // Editar lançamento existente
      setFormData({
        tipo: lancamento.tipo || "receber",
        categoria: lancamento.categoria || "",
        descricao: lancamento.descricao || "",
        id_pessoa: (lancamento.id_pessoa && lancamento.id_pessoa.toString()) || "",
        nome_pessoa: lancamento.nome_pessoa || "",
        documento_pessoa: lancamento.documento_pessoa || "",
        valor_total: lancamento.valor_total ? formatarMoedaInput(lancamento.valor_total.toString()) : "",
        forma_pagamento: lancamento.forma_pagamento || "",
        data_emissao: lancamento.data_emissao ? converterDataParaInput(lancamento.data_emissao) : "",
        data_vencimento: lancamento.data_vencimento ? converterDataParaInput(lancamento.data_vencimento) : "",
        status: lancamento.status === 'vencido' ? 'atrasado' : (lancamento.status || "pendente"),
        data_pagamento: lancamento.data_pagamento ? converterDataParaInput(lancamento.data_pagamento) : "",
        valor_pago: lancamento.valor_pago ? formatarMoedaInput(lancamento.valor_pago.toString()) : "",
        observacoes: lancamento.observacoes || "",
        anexo: lancamento.anexo || ""
      });
      
      setTipoPessoa(lancamento.tipo_pessoa || "cliente");
      setTipoDocumento(lancamento.tipo_documento || "cpf");
    } else {
      // Formulário vazio para novo lançamento
      setFormData({
        tipo: "receber",
        categoria: "",
        descricao: "",
        id_pessoa: "",
        nome_pessoa: "",
        documento_pessoa: "",
        valor_total: "",
        forma_pagamento: "",
        data_emissao: "",
        data_vencimento: "",
        status: "pendente",
        data_pagamento: "",
        valor_pago: "",
        observacoes: "",
        anexo: ""
      });
      
      // Reset dos estados de tipo
      setTipoPessoa("cliente");
      setTipoDocumento("cpf");
    }
    setTentouSalvar(false);
    setOpenForm(true);
  };

  const visualizarLancamento = (lancamento) => {
    setLancamentoVisualizar(lancamento);
    setOpenView(true);
  };

  const fecharFormulario = () => {
    setOpenForm(false);
    setLancamentoSelecionado(null);
  };

  const fecharVisualizacao = () => {
    setOpenView(false);
    setLancamentoVisualizar(null);
  };


  // ========================================
  // FUNÇÕES PARA RELATÓRIOS GERENCIAIS
  // ========================================
  
  const abrirRelatorios = () => {
    setOpenRelatorios(true);
    setRelatorioSelecionado(null);
    // Log de auditoria removido - agora é feito no backend
  };

  const abrirGrafico = () => {
    // Faz scroll para a seção do gráfico
    const elementoGrafico = document.getElementById('secao-grafico');
    if (elementoGrafico) {
      elementoGrafico.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const selecionarRelatorio = (tipo) => {
    setRelatorioSelecionado(tipo);
    carregarDadosRelatorio(tipo);
  };

  const carregarDadosRelatorio = async (tipo) => {
    setLoadingRelatorio(true);
    try {
      let dados = {};

      switch (tipo) {
        case 'contas-pagar':
          dados = await carregarContasPagar();
          break;
        case 'contas-receber':
          dados = await carregarContasReceber();
          break;
        case 'fluxo-caixa':
          dados = await carregarFluxoCaixa();
          break;
        case 'posicao-categoria':
          dados = await carregarPosicaoCategoria();
          break;
        case 'analise-inadimplencia':
          dados = await carregarAnaliseInadimplencia();
          break;
        case 'analise-cobranca':
          dados = await carregarAnaliseCobranca();
          break;
        case 'analise-vencimentos':
          dados = await carregarAnaliseVencimentos();
          break;
        default:
          throw new Error('Tipo de relatório não reconhecido');
      }

      setDadosRelatorio(dados);
    } catch (err) {
      setError('Erro ao carregar dados do relatório');
    } finally {
      setLoadingRelatorio(false);
    }
  };

  // Recarrega automaticamente ao mudar período em Contas a Pagar/Receber
  useEffect(() => {
    if (relatorioSelecionado === 'contas-pagar' || relatorioSelecionado === 'contas-receber') {
      carregarDadosRelatorio(relatorioSelecionado);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtrosRelatorio?.dataInicio,
    filtrosRelatorio?.dataFim,
    filtrosRelatorio?.categoria,
    relatorioSelecionado
  ]);

  useEffect(() => {
    if (relatorioSelecionado !== 'analise-inadimplencia') return undefined;
    const timer = window.setTimeout(() => {
      carregarDadosRelatorio(relatorioSelecionado);
    }, 300);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosInadimplencia, relatorioSelecionado]);

  const buscarRelatorioFinanceiro = async (endpoint, params = {}) => {
    const response = await api.get(`/financeiro/relatorios/${endpoint}`, { params });
    return response.data;
  };

  const carregarContasPagar = async () => {
    return buscarRelatorioFinanceiro('contas-pagar', {
      dataInicio: filtrosRelatorio.dataInicio || undefined,
      dataFim: filtrosRelatorio.dataFim || undefined,
      categoria: filtrosRelatorio.categoria || undefined
    });
  };

  const carregarContasReceber = async () => {
    return buscarRelatorioFinanceiro('contas-receber', {
      dataInicio: filtrosRelatorio.dataInicio || undefined,
      dataFim: filtrosRelatorio.dataFim || undefined,
      categoria: filtrosRelatorio.categoria || undefined
    });
  };

  const carregarFluxoCaixa = async () => {
    return buscarRelatorioFinanceiro('fluxo-caixa');
  };

  const carregarPosicaoCategoria = async () => {
    return buscarRelatorioFinanceiro('posicao-categoria');
  };

  const carregarAnaliseInadimplencia = async () => {
    return buscarRelatorioFinanceiro('inadimplencia', {
      categoria: filtrosInadimplencia.categoria || undefined,
      cliente: filtrosInadimplencia.cliente || undefined,
      diasAtrasoMin: filtrosInadimplencia.diasAtrasoMin || undefined,
      diasAtrasoMax: filtrosInadimplencia.diasAtrasoMax || undefined,
      valorMin: filtrosInadimplencia.valorMin || undefined,
      valorMax: filtrosInadimplencia.valorMax || undefined
    });
  };

  const carregarAnaliseCobranca = async () => {
    return buscarRelatorioFinanceiro('desempenho-cobranca');
  };

  const carregarAnaliseVencimentos = async () => {
    return buscarRelatorioFinanceiro('vencimentos');
  };

  const bloquearCliente = async (cliente) => {
    try {
      if (!cliente.id_pessoa) {
        setError('ID do cliente não encontrado');
        return;
      }

      // Confirmação antes de bloquear
      const confirmacao = window.confirm(
        `Tem certeza que deseja BLOQUEAR o cliente "${cliente.nome_pessoa}"?\n\n` +
        `Valor em atraso: ${formatarMoeda(cliente.valor_total)}\n` +
        `Descrição: ${cliente.descricao}\n\n` +
        `Esta ação irá:\n` +
        `• Alterar o status do cliente para "Bloqueado"\n` +
        `• Adicionar observações sobre a inadimplência\n` +
        `• Registrar a ação no log de auditoria`
      );

      if (!confirmacao) {
        return;
      }

      const headers = getHeaders();
      const hoje = new Date();
      const dataVencimento = cliente.data_vencimento ? new Date(cliente.data_vencimento) : null;
      const diasAtraso = dataVencimento && dataVencimento < hoje ? 
        Math.floor((hoje - dataVencimento) / (1000 * 60 * 60 * 24)) : 0;

      // Preparar dados para atualização do cliente
      const dadosCliente = {
        status: 'Bloqueado',
        observacoes: cliente.observacoes ? 
          `${cliente.observacoes}\n\n--- BLOQUEIO POR INADIMPLÊNCIA ---\nData: ${hoje.toLocaleDateString('pt-BR')}\nValor em atraso: ${formatarMoeda(cliente.valor_total)}\nDias de atraso: ${diasAtraso}\nDescrição: ${cliente.descricao}\nCategoria: ${cliente.categoria}\nVencimento: ${dataVencimento ? dataVencimento.toLocaleDateString('pt-BR') : 'N/A'}\nMotivo: Cliente inadimplente com pagamento em atraso.` :
          `--- BLOQUEIO POR INADIMPLÊNCIA ---\nData: ${hoje.toLocaleDateString('pt-BR')}\nValor em atraso: ${formatarMoeda(cliente.valor_total)}\nDias de atraso: ${diasAtraso}\nDescrição: ${cliente.descricao}\nCategoria: ${cliente.categoria}\nVencimento: ${dataVencimento ? dataVencimento.toLocaleDateString('pt-BR') : 'N/A'}\nMotivo: Cliente inadimplente com pagamento em atraso.`
      };

      // Atualizar o cliente
      // Log de auditoria removido - agora é feito no backend
      // env.API_URL já contém '/api', então não adicionar '/api' novamente
      await axios.put(`${env.API_URL}/clientes/${cliente.id_pessoa}`, dadosCliente, { headers });
      setSuccess("Cliente bloqueado com sucesso!");
      
      // Recarregar dados do relatório
      if (relatorioSelecionado) {
        // Log de auditoria removido - agora é feito no backend
        await carregarDadosRelatorio(relatorioSelecionado);
      }
    } catch (error) {
      setError(`Erro ao bloquear cliente: ${error.message}`);
    }
  };

  const imprimirRelatorio = () => {
    if (!relatorioSelecionado || !dadosRelatorio.titulo) {
              setError('Nenhum relatório selecionado para impressão');
      return;
    }

    const printWindow = window.open('', '_blank');
    const html = gerarHTMLRelatorio();
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const gerarHTMLRelatorio = () => {
    const { titulo, descricao } = dadosRelatorio;
    
    return `
      <!DOCTYPE html>
      <html lang="pt-BR">
      <head>
        <meta charset="UTF-8">
        <title>${titulo}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .description { font-size: 14px; color: #666; margin-bottom: 20px; }
          .info { margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 11px; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .total { font-weight: bold; background-color: #e8f5e8; }
          .summary { background-color: #f0f8ff; padding: 15px; border-radius: 5px; margin-top: 20px; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">${titulo}</div>
          <div class="description">${descricao}</div>
          <div>Data de impressão: ${new Date().toLocaleDateString('pt-BR')}</div>
        </div>
        
        <div class="info">
          <strong>Período:</strong> ${
            dadosRelatorio.periodo?.dataInicio ||
            dadosRelatorio.dataReferencia ||
            'Todos'
          } a ${
            dadosRelatorio.periodo?.dataFim ||
            dadosRelatorio.dataReferencia ||
            'Todos'
          }
        </div>
        
        ${gerarTabelaRelatorio()}
        
        <div class="summary">
          <h3>Resumo</h3>
          ${gerarResumoRelatorio()}
        </div>
      </body>
      </html>
    `;
  };

  const gerarTabelaRelatorio = () => {
    const { dados, colunas } = dadosRelatorio;
    
    if (!Array.isArray(dados)) return '<p>Dados não disponíveis em formato de tabela</p>';

    const valorColuna = (item, coluna) => {
      switch (coluna) {
        case 'Cliente':
        case 'Fornecedor':
          return item.nome_pessoa || 'N/A';
        case 'Descrição':
          return item.descricao || 'N/A';
        case 'Categoria':
          return item.categoria || 'N/A';
        case 'Tipo':
          return item.tipo === 'receber' ? 'Receber' : 'Pagar';
        case 'Data Vencimento':
          return item.data_vencimento ? formatarData(item.data_vencimento) : 'N/A';
        case 'Data Pagamento':
          return item.data_pagamento ? formatarData(item.data_pagamento) : 'N/A';
        case 'Dias Atraso':
          return `${item.dias_atraso ?? 0} dias`;
        case 'Dias Restantes':
          return `${item.dias_restantes ?? 0} dias`;
        case 'Status':
          return item.status || 'N/A';
        case 'Valor Original':
          return formatarMoeda(item.valor_original ?? item.valor_total ?? 0);
        case 'Valor Realizado':
          return formatarMoeda(item.valor_realizado ?? 0);
        case 'Saldo em Aberto':
        case 'Valor':
          return formatarMoeda(item.saldo_aberto ?? item.valor_realizado ?? item.valor_total ?? 0);
        case 'A Receber':
          return formatarMoeda(item.receber || 0);
        case 'A Pagar':
          return formatarMoeda(item.pagar || 0);
        case 'Saldo':
          return formatarMoeda(item.saldo || 0);
        default:
          return 'N/A';
      }
    };
    
    return `
      <table>
        <thead>
          <tr>
            ${colunas.map(col => `<th>${col}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${dados.map(item => `
            <tr>
              ${colunas.map(coluna => `<td>${valorColuna(item, coluna)}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
  };

  const gerarResumoRelatorio = () => {
    const { dados, total } = dadosRelatorio;
    
    if (Array.isArray(dados)) {
      return `
        <p><strong>Total de registros:</strong> ${dados.length}</p>
        <p><strong>Valor total:</strong> R$ ${(total && total.toFixed(2)) || '0,00'}</p>
      `;
    }
    
    return '<p>Resumo não disponível para este tipo de relatório</p>';
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleProximo = () => {
    if (activeTab < 2) { // Se houver mais de uma guia
      setActiveTab(activeTab + 1);
    }
  };

  const handleAnterior = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };

  const handleFechar = () => {
    setFormData({
      tipo: "receber",
      categoria: "",
      descricao: "",
      id_pessoa: "",
      nome_pessoa: "",
      documento_pessoa: "",
      valor_total: "",
      forma_pagamento: "",
      data_emissao: "",
      data_vencimento: "",
      status: "pendente",
      data_pagamento: "",
      valor_pago: "",
      observacoes: "",
      anexo: ""
    });
    setTipoPessoa("cliente");
    setTipoDocumento("cpf");
    setActiveTab(0);
    fecharFormulario();
  };

  // useEffect para validar colaborador com CNPJ
  useEffect(() => {
    if (tipoPessoa === "colaborador" && tipoDocumento === "cnpj" && formData.nome_pessoa) {
      const colaboradorSelecionado = colaboradores.find(col => col.nome_completo === formData.nome_pessoa);
      if (!(colaboradorSelecionado && colaboradorSelecionado.cnpj)) {
        setFormError("Este colaborador não possui CNPJ cadastrado. Selecione CPF ou escolha outro colaborador.");
      } else {
        setFormError(null);
      }
    } else {
      setFormError(null);
    }
  }, [tipoPessoa, tipoDocumento, formData.nome_pessoa, colaboradores]);

  // useEffect para garantir que formData sempre tenha valores válidos
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      nome_pessoa: prev.nome_pessoa || "",
      descricao: prev.descricao || "",
      categoria: prev.categoria || "",
      id_pessoa: prev.id_pessoa || "",
      documento_pessoa: prev.documento_pessoa || "",
      valor_total: prev.valor_total || "",
      forma_pagamento: prev.forma_pagamento || "",
      data_emissao: prev.data_emissao || "",
      data_vencimento: prev.data_vencimento || "",
      status: prev.status || "pendente",
      data_pagamento: prev.data_pagamento || "",
      valor_pago: prev.valor_pago || "",
      observacoes: prev.observacoes || "",
      anexo: prev.anexo || ""
    }));
  }, []);

  // Função para validar campos obrigatórios
  // ========================================
  // FUNÇÕES DE VALIDAÇÃO
  // ========================================
  
  const obterErrosFormulario = () => {
    const erros = {};
    const dataEmissao = parseDataLocal(formatarDataParaInput(formData.data_emissao));
    const dataVencimento = parseDataLocal(formatarDataParaInput(formData.data_vencimento));
    const valorTotal = Number(removerFormatacao(formData.valor_total || '')) / 100;
    const valorPago = Number(removerFormatacao(formData.valor_pago || '')) / 100;

    if (!formData.tipo) erros.tipo = 'Tipo é obrigatório.';
    if (!formData.categoria) erros.categoria = 'Categoria é obrigatória.';
    if (!(formData.descricao || '').trim()) erros.descricao = 'Descrição é obrigatória.';
    if (!(formData.nome_pessoa || '').trim()) erros.nome_pessoa = 'Cliente ou colaborador é obrigatório.';
    if (!(valorTotal > 0)) erros.valor_total = 'Informe um valor maior que zero.';
    if (!dataEmissao) erros.data_emissao = 'Informe uma data de emissão válida.';
    if (!dataVencimento) erros.data_vencimento = 'Informe uma data de vencimento válida.';
    if (dataEmissao && dataVencimento && dataEmissao > dataVencimento) {
      erros.data_vencimento = 'O vencimento não pode ser anterior à emissão.';
    }
    if (!formData.status) erros.status = 'Status é obrigatório.';
    if (valorPago > valorTotal) erros.valor_pago = 'O valor pago não pode superar o valor total.';
    if (formData.status === 'pago' && valorPago !== valorTotal) {
      erros.valor_pago = 'Para status Pago, o valor pago deve ser igual ao total.';
    }
    if (formData.status === 'parcial' && (!(valorPago > 0) || valorPago >= valorTotal)) {
      erros.valor_pago = 'Para status Parcial, informe um valor entre zero e o total.';
    }
    if (['pago', 'parcial'].includes(formData.status) &&
        !parseDataLocal(formatarDataParaInput(formData.data_pagamento))) {
      erros.data_pagamento = 'A data do pagamento é obrigatória para este status.';
    }
    if (tipoPessoa === "colaborador" && tipoDocumento === "cnpj" && formData.nome_pessoa) {
      const colaborador = colaboradores.find((item) => item.nome_completo === formData.nome_pessoa);
      if (!colaborador?.cnpj) erros.nome_pessoa = 'O colaborador selecionado não possui CNPJ.';
    }

    return erros;
  };

  // ========================================
  // FUNÇÕES CRUD (CREATE, READ, UPDATE, DELETE)
  // ========================================
  
  const salvarLancamento = async () => {
    const headers = getHeaders();
    setTentouSalvar(true);
    const erros = obterErrosFormulario();
    if (Object.keys(erros).length > 0) {
      setError('Revise os campos obrigatórios e os valores informados.');
      const camposAbaBasica = ['tipo', 'categoria', 'descricao', 'nome_pessoa'];
      setActiveTab(Object.keys(erros).some(campo => camposAbaBasica.includes(campo)) ? 0 : 1);
      return;
    }
    
    // Função para converter valor formatado para número
    const converterValorFormatado = (valorFormatado) => {
      if (!valorFormatado) return 0;
      // Remove formatação e converte para número
      const valorNumerico = removerFormatacao(valorFormatado);
      return parseFloat(valorNumerico) / 100; // Divide por 100 pois formatarMoedaInput trabalha com centavos
    };

    const payload = {
      tipo: formData.tipo,
      descricao: formData.descricao || "",
      categoria: formData.categoria || "",
      id_pessoa: formData.id_pessoa || "",
      nome_pessoa: formData.nome_pessoa || "",
      documento_pessoa: formData.documento_pessoa || "",
      tipo_pessoa: tipoPessoa, // "cliente" ou "colaborador"
      tipo_documento: tipoDocumento, // "cpf" ou "cnpj"
      valor_total: converterValorFormatado(formData.valor_total),
      forma_pagamento: formData.forma_pagamento || "Outros",
      data_emissao: formatarDataParaInput(formData.data_emissao),
      data_vencimento: formatarDataParaInput(formData.data_vencimento),
      status: formData.status || "pendente",
      data_pagamento: formatarDataParaInput(formData.data_pagamento) || null,
      valor_pago: formData.valor_pago ? converterValorFormatado(formData.valor_pago) : null,
      observacoes: formData.observacoes || "",
      anexo: formData.anexo || "",
      st_codHashEmpresa: st_codHashEmpresa
    };

    try {
      if (lancamentoSelecionado) {
        // Log de auditoria removido - agora é feito no backend
        await api.put(`/financeiro/${lancamentoSelecionado.id_lancamento}`, payload, { headers });
        setSuccess("Lançamento atualizado com sucesso!");
      } else {
        // Log de auditoria removido - agora é feito no backend
        await api.post(`/financeiro`, payload, { headers });
        setSuccess("Lançamento criado com sucesso!");
      }
      
      carregarLancamentos();
      fecharFormulario();
    } catch (error) {
      
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
      } else {
        const errorMessage = (error.response && error.response.data && error.response.data.message) || 
                           (error.response && error.response.data && error.response.data.error) || 
                           error.message || 
                           "Erro desconhecido";
        setError(`Erro ao salvar lançamento: ${errorMessage}`);
      }
    }
  };

  const deletarLancamento = async (id) => {
    const headers = getHeaders();

    if (window.confirm("Deseja realmente excluir este lançamento?")) {
      try {
        const lancamento = lancamentos.find(l => l.id_lancamento === id);
        const descricaoExclusao = `Excluiu o lançamento ${(lancamento && lancamento.descricao) || 'Lançamento'}`;
        // Log de auditoria removido - agora é feito no backend
        
        await api.delete(`/financeiro/${id}`, { headers });
        setSuccess("Lançamento excluído com sucesso!");
        carregarLancamentos();
      } catch (err) {
        if (err.response && err.response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          window.location.href = "/login";
        } else {
          alert(`Erro ao excluir lançamento: ${(err.response && err.response.data && err.response.data.message) || err.message}`);
        }
      }
    }
  };

  // ========================================
  // FUNÇÕES DE FILTRO E ORDENAÇÃO
  // ========================================
  
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const lancamentosFiltrados = lancamentos;

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros({ ...filtros, [name]: value });
    setPage(0);
  };

  // ========================================
  // VERIFICAÇÃO DE ACESSO
  // ========================================

  // ========================================
  // RENDERIZAÇÃO DO COMPONENTE
  // ========================================

  const errosCampos = tentouSalvar ? obterErrosFormulario() : {};

  return (
    <AcessoRestrito modulo="financeiro">
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Box interno com margens de 1,5% */}
        <Box sx={{
          margin: '0 1.5% 1.5% 1.5%',  // 1,5% nas laterais e inferior
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            p: { xs: 2, sm: 3 }, 
            pb: 2,
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography 
              variant="h5" 
              mb={1} 
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <AttachMoneyIcon sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                color: '#4CAF50' // Cor verde para financeiro
              }} />
              Financeiro
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button 
                variant="outlined" 
                onClick={abrirRelatorios}
                startIcon={<BarChartIcon />}
                sx={{
                  borderColor: (theme) => theme.palette.primary.main,
                  color: (theme) => theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                    borderColor: (theme) => theme.palette.primary.dark
                  }
                }}
              >
                📋 Relatórios Gerenciais
              </Button>
              <Button 
                variant="contained" 
                onClick={() => abrirFormulario()}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { xs: 'auto', sm: '200px' },
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? '#ffffff'
                    : theme.palette.primary.main,
                  color: (theme) => theme.palette.mode === 'dark'
                    ? '#000000'
                    : theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? '#f5f5f5'
                      : theme.palette.primary.dark,
                  }
                }}
              >
                <AddIcon /> Novo Lançamento
              </Button>
            </Box>
          </Box>

          {/* Container da Tabela */}
          <TableContainer
            component={Paper}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'auto',
              height: {
                xs: 'calc(90vh - 140px)',
                sm: 'calc(90vh - 120px)',
                md: 'calc(90vh - 120px)',
                lg: 'calc(90vh - 120px)'
              },
              maxHeight: {
                xs: 'calc(90vh - 140px)',
                sm: 'calc(90vh - 120px)',
                md: 'calc(90vh - 120px)',
                lg: 'calc(90vh - 120px)'
              },
              width: '100%',
              minWidth: { xs: '100%', sm: '600px' },
              maxWidth: '100%',
              boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: 2,
              border: 'none'
            }}
          >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1
            }}
          >
            <CircularProgress />
          </Box>
        )}
        
          <Table sx={{ 
            minWidth: { xs: 800, sm: 650 }, 
            tableLayout: 'fixed',
            width: '100%'
          }}>
            <TableHead sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: (theme) => theme.palette.background.paper,
              '& .MuiTableCell-root': {
                backgroundColor: (theme) => theme.palette.background.paper,
                borderBottom: 2,
                borderBottomColor: 'divider'
              }
            }}>
              <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "descricao"}
                  direction={orderBy === "descricao" ? order : "asc"}
                  onClick={() => handleRequestSort("descricao")}
                >
                  Descrição
                </TableSortLabel>
                <TextField
                  name="descricao"
                  size="small"
                  fullWidth
                  value={filtros.descricao}
                  onChange={handleFiltroChange}
                  placeholder="Filtrar por descrição"
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "nome_pessoa"}
                  direction={orderBy === "nome_pessoa" ? order : "asc"}
                  onClick={() => handleRequestSort("nome_pessoa")}
                >
                  Nome
                </TableSortLabel>
                <TextField
                  name="cliente"
                  size="small"
                  fullWidth
                  value={filtros.cliente}
                  onChange={handleFiltroChange}
                  placeholder="Filtrar por nome"
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "categoria"}
                  direction={orderBy === "categoria" ? order : "asc"}
                  onClick={() => handleRequestSort("categoria")}
                >
                  Categoria
                </TableSortLabel>
                <FormControl fullWidth size="small">
                  <Select
                    name="categoria"
                    value={filtros.categoria}
                    onChange={handleFiltroChange}
                    displayEmpty
                    disabled={loadingCategorias || Boolean(erroCategorias)}
                    sx={{ mt: 1 }}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {categorias.map(categoria => (
                      <MenuItem key={categoria.categoriaId} value={categoria.st_nomeCategoria}>
                        {categoria.st_nomeCategoria}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "tipo"}
                  direction={orderBy === "tipo" ? order : "asc"}
                  onClick={() => handleRequestSort("tipo")}
                >
                  Tipo
                </TableSortLabel>
                <FormControl fullWidth size="small">
                  <Select
                    name="tipo"
                    value={filtros.tipo}
                    onChange={handleFiltroChange}
                    displayEmpty
                    sx={{ mt: 1 }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="receber">Receber</MenuItem>
                    <MenuItem value="pagar">Pagar</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "data_emissao"}
                  direction={orderBy === "data_emissao" ? order : "asc"}
                  onClick={() => handleRequestSort("data_emissao")}
                >
                  Data
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "data_vencimento"}
                  direction={orderBy === "data_vencimento" ? order : "asc"}
                  onClick={() => handleRequestSort("data_vencimento")}
                >
                  Vencimento
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "valor_total"}
                  direction={orderBy === "valor_total" ? order : "asc"}
                  onClick={() => handleRequestSort("valor_total")}
                >
                  Valor
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={() => handleRequestSort("status")}
                >
                  Status
                </TableSortLabel>
                <FormControl fullWidth size="small">
                  <Select
                    name="status"
                    value={filtros.status}
                    onChange={handleFiltroChange}
                    displayEmpty
                    sx={{ mt: 1 }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="pendente">Pendente</MenuItem>
                    <MenuItem value="pago">Pago</MenuItem>
                    <MenuItem value="atrasado">Atrasado</MenuItem>
                    <MenuItem value="parcial">Parcial</MenuItem>
                    <MenuItem value="cancelado">Cancelado</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lancamentosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {loading ? (
                    <CircularProgress />
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      {lancamentos.length === 0 
                        ? "Nenhum lançamento encontrado. Clique em 'Novo Lançamento' para começar."
                        : "Nenhum lançamento corresponde aos filtros aplicados."
                      }
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              lancamentosFiltrados.map((lancamento) => (
                  <TableRow key={lancamento.id_lancamento || lancamento.id}>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lancamento.descricao}
                    </TableCell>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lancamento.nome_pessoa}
                    </TableCell>
                    <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lancamento.categoria}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={lancamento.tipo === "receber" ? "Receber" : "Pagar"}
                        color={lancamento.tipo === "receber" ? "success" : "error"}
                        size="small"
                        variant="filled"
                        sx={{
                          fontWeight: "bold",
                          minWidth: "80px"
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {lancamento.data_emissao ? formatarData(lancamento.data_emissao) : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {lancamento.data_vencimento ? formatarData(lancamento.data_vencimento) : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          color: lancamento.tipo === "receber" ? "success.main" : "error.main",
                          fontWeight: "bold"
                        }}
                      >
                        {formatarMoeda(lancamento.valor_total)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={lancamento.status === "pago" ? "Pago" : 
                               lancamento.status === "pendente" ? "Pendente" :
                               lancamento.status === "atrasado" ? "Atrasado" :
                               lancamento.status === "parcial" ? "Parcial" :
                               lancamento.status === "cancelado" ? "Cancelado" : "Pendente"}
                        color={lancamento.status === "pago" ? "success" : 
                               lancamento.status === "pendente" ? "warning" :
                               lancamento.status === "atrasado" ? "error" :
                               lancamento.status === "parcial" ? "info" :
                               lancamento.status === "cancelado" ? "default" : "warning"}
                        size="small"
                        variant="filled"
                        sx={{
                          fontWeight: "bold",
                          minWidth: "80px"
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Visualizar">
                          <IconButton size="small" onClick={() => visualizarLancamento(lancamento)}>
                            <Visibility fontSize="small" className="material-icon-visibility" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirFormulario(lancamento)}>
                            <Edit fontSize="small" className="material-icon-edit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={lancamento.status === "pago" ? "Imprimir Recibo" : "Imprimir Recibo (Disponível apenas para lançamentos pagos)"}>
                          <span>
                            <IconButton 
                              size="small" 
                              onClick={() => imprimirComprovante(lancamento)}
                              disabled={lancamento.status !== "pago"}
                              sx={{
                                opacity: lancamento.status !== "pago" ? 0.3 : 1,
                                cursor: lancamento.status !== "pago" ? "not-allowed" : "pointer"
                              }}
                            >
                              <Print fontSize="small" className="material-icon-print" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => deletarLancamento(lancamento.id_lancamento || lancamento.id)}>
                            <Delete fontSize="small" className="material-icon-delete" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação Fixa */}
      <TablePagination
        component="div"
        count={totalLancamentos}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
        labelRowsPerPage="Itens por página"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          width: '100%',
          px: 3,
          py: 1.5,
          boxSizing: 'border-box',
          zIndex: 1000,
          minHeight: '60px'
        }}
      />
        </Box>
      </Box>

      {/* Dialog de Cadastro/Edição */}
      <Dialog
        open={openForm}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return; // impede fechar ao clicar no backdrop
          fecharFormulario();
        }}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        disablePortal
        hideBackdrop={false}
        sx={{
          '& .MuiDialog-backdrop': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
        PaperProps={{
          sx: {
            height: '550px',
            position: 'relative'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          fontSize: '1.25rem',
          fontWeight: 500
        }}>
          {lancamentoSelecionado ? "Editar Lançamento" : "Novo Lançamento"}
          <IconButton
            onClick={fecharFormulario}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          height: 'calc(100% - 140px)',
          overflow: 'auto',
          padding: 2,
          '&.MuiDialogContent-root': {
            paddingTop: 2
          },
          position: 'relative',
          top: 0,
          marginBottom: '60px',
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem'
          },
          '& .MuiInputBase-input': {
            fontSize: '0.875rem'
          },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem'
          }
        }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Informações Básicas" />
              <Tab label="Valores e Datas" />
              <Tab label="Observações e Anexos" />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                * Campos obrigatórios
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ width: '50%' }} required error={!!errosCampos.tipo}>
                  <InputLabel>Tipo *</InputLabel>
                  <Select
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleInputChange}
                    label="Tipo *"
                    required
                  >
                    <MenuItem value="receber">Receber</MenuItem>
                    <MenuItem value="pagar">Pagar</MenuItem>
                  </Select>
                  <FormHelperText>{errosCampos.tipo}</FormHelperText>
                </FormControl>
                <FormControl sx={{ width: '50%' }} required error={!!errosCampos.categoria}>
                  <InputLabel>Categoria *</InputLabel>
                  <Select
                    name="categoria"
                    value={formData.categoria}
                    onChange={handleInputChange}
                    label="Categoria *"
                    required
                    disabled={loadingCategorias || Boolean(erroCategorias) || categorias.length === 0}
                  >
                    {lancamentoSelecionado?.categoria &&
                      !categorias.some(categoria => categoria.st_nomeCategoria === lancamentoSelecionado.categoria) && (
                        <MenuItem value={lancamentoSelecionado.categoria} disabled>
                          {lancamentoSelecionado.categoria} (categoria histórica indisponível)
                        </MenuItem>
                      )}
                    {categorias.map(categoria => (
                      <MenuItem key={categoria.categoriaId} value={categoria.st_nomeCategoria}>
                        {categoria.st_nomeCategoria}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errosCampos.categoria ||
                      (loadingCategorias ? 'Carregando categorias...' : '') ||
                      erroCategorias ||
                      (!loadingCategorias && categorias.length === 0
                        ? 'Nenhuma categoria disponível no cadastro.'
                        : '')}
                  </FormHelperText>
                </FormControl>
              </Box>
              <TextField
                label="Descrição *"
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                fullWidth
                required
                multiline
                rows={2}
                error={!!errosCampos.descricao}
                helperText={errosCampos.descricao}
              />
              
              {/* Seleção de Tipo de Pessoa e Tipo de Documento */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl component="fieldset" sx={{ flex: 1 }}>
                  <FormLabel component="legend">Tipo de Pessoa</FormLabel>
                  <RadioGroup
                    row
                    name="tipoPessoa"
                    value={tipoPessoa}
                    onChange={handleTipoPessoaChange}
                  >
                    <FormControlLabel value="cliente" control={<Radio />} label="Cliente" />
                    <FormControlLabel value="colaborador" control={<Radio />} label="Colaborador" />
                  </RadioGroup>
                </FormControl>

                {/* Seleção de Tipo de Documento (apenas para colaboradores) */}
                <FormControl component="fieldset" sx={{ flex: 1, opacity: tipoPessoa === "cliente" ? 0.5 : 1 }}>
                  <FormLabel component="legend">Tipo de Documento</FormLabel>
                  <RadioGroup
                    row
                    name="tipoDocumento"
                    value={tipoDocumento}
                    onChange={handleTipoDocumentoChange}
                  >
                    <FormControlLabel 
                      value="cpf" 
                      control={<Radio />} 
                      label="CPF"
                      disabled={tipoPessoa === "cliente"}
                    />
                    <FormControlLabel 
                      value="cnpj" 
                      control={<Radio />} 
                      label="CNPJ da Empresa"
                      disabled={
                        tipoPessoa === "cliente" || (
                          !formData.nome_pessoa || 
                          !(colaboradores.find(col => col.nome_completo === (formData.nome_pessoa || "")) && colaboradores.find(col => col.nome_completo === (formData.nome_pessoa || "")).cnpj)
                        )
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* Mensagem de erro (apenas para colaboradores) */}
              {tipoPessoa === "colaborador" && formError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {formError}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Autocomplete
                  options={
                    tipoPessoa === "cliente"
                      ? Array.from(new Set(clientes.map(c => c.nome_completo)))
                      : Array.from(new Set(colaboradores.map(c => c.nome_completo)))
                  }
                  value={formData.nome_pessoa || ""}
                  onChange={(event, newValue) => handleAutocompleteChange("nome_pessoa", newValue)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={`Nome do ${tipoPessoa === "cliente" ? "Cliente" : "Colaborador"} *`}
                      required
                      error={!!errosCampos.nome_pessoa}
                      helperText={errosCampos.nome_pessoa}
                    />
                  )}
                  sx={{ width: '50%' }}
                />
                <TextField
                  label={tipoDocumento === "cpf" ? "CPF" : "CNPJ"}
                  name="documento_pessoa"
                  value={formData.documento_pessoa || ""}
                  onChange={handleInputChange}
                  disabled={!!formData.nome_pessoa}
                  helperText={formData.nome_pessoa ? "Preenchido automaticamente" : ""}
                  sx={{ width: '50%' }}
                />
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Valor Total *"
                  name="valor_total"
                  value={formData.valor_total || ""}
                  onChange={(e) => handleValorChange("valor_total", e.target.value)}
                  placeholder="R$ 0,00"
                  sx={{ width: '50%' }}
                  required
                  error={!!errosCampos.valor_total}
                  helperText={errosCampos.valor_total}
                />
                <FormControl sx={{ width: '50%' }}>
                  <InputLabel>Forma de Pagamento</InputLabel>
                  <Select
                    name="forma_pagamento"
                    value={formData.forma_pagamento}
                    onChange={handleInputChange}
                    label="Forma de Pagamento"
                  >
                    {FORMAS_PAGAMENTO.map((opcao, index) => (
                      <MenuItem key={`${opcao}-${index}`} value={opcao}>{opcao}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ width: '50%' }} required error={!!errosCampos.status}>
                  <InputLabel>Status *</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="Status *"
                  >
                    <MenuItem value="pendente">Pendente</MenuItem>
                    <MenuItem value="pago">Pago</MenuItem>
                    <MenuItem value="atrasado">Atrasado</MenuItem>
                    <MenuItem value="parcial">Parcial</MenuItem>
                    <MenuItem value="cancelado">Cancelado</MenuItem>
                  </Select>
                  <FormHelperText>{errosCampos.status}</FormHelperText>
                </FormControl>
                <TextField
                  label="Valor Pago"
                  name="valor_pago"
                  value={formData.valor_pago || ""}
                  onChange={(e) => handleValorChange("valor_pago", e.target.value)}
                  placeholder="R$ 0,00"
                  sx={{ width: '50%' }}
                  error={!!errosCampos.valor_pago}
                  helperText={errosCampos.valor_pago}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Data de Emissão *"
                  name="data_emissao"
                  value={formatarDataParaExibicao(formData.data_emissao)}
                  onChange={(e) => handleDataChange("data_emissao", e.target.value)}
                  placeholder="DD/MM/AAAA"
                  sx={{ width: '33%' }}
                  required
                  error={!!errosCampos.data_emissao}
                  helperText={errosCampos.data_emissao}
                  inputProps={{
                    maxLength: 10
                  }}
                />
                <TextField
                  label="Data de Vencimento *"
                  name="data_vencimento"
                  value={formatarDataParaExibicao(formData.data_vencimento)}
                  onChange={(e) => handleDataChange("data_vencimento", e.target.value)}
                  placeholder="DD/MM/AAAA"
                  sx={{ width: '33%' }}
                  required
                  error={!!errosCampos.data_vencimento}
                  helperText={errosCampos.data_vencimento}
                  inputProps={{
                    maxLength: 10
                  }}
                />
                <TextField
                  label="Data do Pagamento"
                  name="data_pagamento"
                  value={formatarDataParaExibicao(formData.data_pagamento)}
                  onChange={(e) => handleDataChange("data_pagamento", e.target.value)}
                  placeholder="DD/MM/AAAA"
                  sx={{ width: '33%' }}
                  error={!!errosCampos.data_pagamento}
                  helperText={errosCampos.data_pagamento}
                  inputProps={{
                    maxLength: 10
                  }}
                />
              </Box>
            </Box>
          )}

          {activeTab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Observações"
                name="observacoes"
                value={formData.observacoes}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={4}
              />
              <TextField
                label="Anexo (URL)"
                name="anexo"
                value={formData.anexo}
                onChange={handleInputChange}
                placeholder="https://exemplo.com/arquivo.pdf"
                helperText="Cole aqui o link do arquivo anexado"
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 2,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1,
          height: '60px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body2" color="text.secondary">
            {new Date().toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
          <Box>
            <Button 
              variant="contained" 
              color="primary"
              onClick={fecharFormulario}
            >
              FECHAR
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={salvarLancamento}
              disabled={loadingCategorias || Boolean(erroCategorias) || categorias.length === 0}
              sx={{ ml: 1 }}
            >
              SALVAR
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dialog de Visualização */}
      <Dialog
        open={openView}
        onClose={(event, reason) => {
          if (reason === 'backdropClick') return; // impede fechar ao clicar no backdrop
          fecharVisualizacao();
        }}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown
        disablePortal
        hideBackdrop={false}
        sx={{
          '& .MuiDialog-backdrop': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
        PaperProps={{
          sx: {
            height: '550px',
            position: 'relative'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          fontSize: '1.25rem',
          fontWeight: 500
        }}>
          Visualizar Lançamento
          <IconButton
            onClick={fecharVisualizacao}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          height: 'calc(100% - 140px)',
          overflow: 'auto',
          padding: 2,
          '&.MuiDialogContent-root': {
            paddingTop: 2
          },
          position: 'relative',
          top: 0,
          marginBottom: '60px',
          '& .MuiInputLabel-root': {
            fontSize: '0.875rem'
          },
          '& .MuiInputBase-input': {
            fontSize: '0.875rem'
          },
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem'
          }
        }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Informações Básicas" />
              <Tab label="Valores e Datas" />
              <Tab label="Observações" />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Descrição"
                value={(lancamentoVisualizar && lancamentoVisualizar.descricao) || ""}
                fullWidth
                disabled
                multiline
                rows={2}
                InputLabelProps={{
                  shrink: true
                }}
                inputProps={{
                  sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Tipo"
                  value={(lancamentoVisualizar && lancamentoVisualizar.tipo) === "receber" ? "Receber" : "Pagar"}
                  sx={{ width: '50%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
                <TextField
                  label="Categoria"
                  value={(lancamentoVisualizar && lancamentoVisualizar.categoria) || ""}
                  sx={{ width: '50%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Nome da Pessoa"
                  value={(lancamentoVisualizar && lancamentoVisualizar.nome_pessoa) || ""}
                  sx={{ width: '50%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
                <TextField
                  label="Documento"
                  value={(lancamentoVisualizar && lancamentoVisualizar.documento_pessoa) ? formatarCPFCNPJ(lancamentoVisualizar.documento_pessoa) : ""}
                  sx={{ width: '50%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Valor Total"
                  value={(lancamentoVisualizar && lancamentoVisualizar.valor_total) ? formatarMoeda(lancamentoVisualizar.valor_total) : ""}
                  sx={{ width: '50%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
                <TextField
                  label="Forma de Pagamento"
                  value={(lancamentoVisualizar && lancamentoVisualizar.forma_pagamento) || ""}
                  sx={{ width: '50%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Status"
                  value={(lancamentoVisualizar && lancamentoVisualizar.status) || ""}
                  sx={{ width: '50%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
                <TextField
                  label="Valor Pago"
                  value={(lancamentoVisualizar && lancamentoVisualizar.valor_pago) ? formatarMoeda(lancamentoVisualizar.valor_pago) : ""}
                  sx={{ width: '50%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Data de Emissão"
                  value={(lancamentoVisualizar && lancamentoVisualizar.data_emissao) ? formatarData(lancamentoVisualizar.data_emissao) : ""}
                  sx={{ width: '33%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
                <TextField
                  label="Data de Vencimento"
                  value={(lancamentoVisualizar && lancamentoVisualizar.data_vencimento) ? formatarData(lancamentoVisualizar.data_vencimento) : ""}
                  sx={{ width: '33%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
                <TextField
                  label="Data do Pagamento"
                  value={(lancamentoVisualizar && lancamentoVisualizar.data_pagamento) ? formatarData(lancamentoVisualizar.data_pagamento) : ""}
                  sx={{ width: '33%' }}
                  disabled
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                  }}
                />
              </Box>
            </Box>
          )}

          {activeTab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Observações"
                value={(lancamentoVisualizar && lancamentoVisualizar.observacoes) || ""}
                fullWidth
                disabled
                multiline
                rows={4}
                InputLabelProps={{
                  shrink: true
                }}
                inputProps={{
                  sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                }}
              />
              <TextField
                label="Anexo (URL)"
                value={(lancamentoVisualizar && lancamentoVisualizar.anexo) || ""}
                fullWidth
                disabled
                InputLabelProps={{
                  shrink: true
                }}
                inputProps={{
                  sx: { backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 2,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1,
          height: '60px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="body2" color="text.secondary">
            {new Date().toLocaleString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              color="secondary"
              startIcon={<Print />}
              onClick={imprimirComprovante}
            >
              IMPRIMIR COMPROVANTE
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={fecharVisualizacao}
            >
              FECHAR
            </Button>
          </Box>
        </DialogActions>
      </Dialog>


      {/* Dialog de Relatórios Gerenciais */}
      <Dialog
        open={openRelatorios}
        onClose={fecharRelatorios}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: '1242px',
            height: '90vh',
            maxHeight: '90vh',
            '& .MuiDialog-backdrop': {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderBottom: '2px solid',
          borderColor: (theme) => theme.palette.primary.dark
        }}>
          <Typography variant="h6" fontWeight="bold">
            📋 Relatórios Gerenciais
          </Typography>
          <IconButton
            onClick={fecharRelatorios}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(90vh - 120px)',
          '&.MuiDialogContent-root': {
            padding: 0
          }
        }}>
          {!relatorioSelecionado ? (
            // Tela de seleção de relatórios
            <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
              <Typography variant="h5" fontWeight="bold" mb={3} textAlign="center">
                Selecione um Relatório
              </Typography>
              
              <Grid container spacing={3} justifyContent="center">
                <Grid 
                  item 
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '50%',
                      md: '33.33%',
                      lg: '25%'
                    },
                    p: 1
                  }}
                >
                  <Paper 
                    elevation={3}
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      height: "250px",
                      width: "100%",
                      minWidth: "280px",
                      "&:hover": {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => selecionarRelatorio('contas-pagar')}
                  >
                    <Typography variant="h4" color="primary" mb={1}>
                      💰
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" mb={1} textAlign="center">
                      Contas a Pagar
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Detalha todas as obrigações financeiras a serem pagas.
                    </Typography>
                  </Paper>
                </Grid>

              <Grid 
                  item 
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '50%',
                      md: '33.33%',
                      lg: '25%'
                    },
                    p: 1
                  }}
                >
                  <Paper 
                    elevation={3}
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      height: "250px",
                      width: "100%",
                      minWidth: "280px",
                      "&:hover": {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => selecionarRelatorio('contas-receber')}
                  >
                    <Typography variant="h4" color="primary" mb={1}>
                      💳
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" mb={1} textAlign="center">
                      Contas a Receber
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Lista todos os valores a serem recebidos de clientes, detalhando datas de vencimento e valores.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid 
                  item 
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '50%',
                      md: '33.33%',
                      lg: '25%'
                    },
                    p: 1
                  }}
                >
                  <Paper 
                    elevation={3}
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      height: "250px",
                      width: "100%",
                      minWidth: "280px",
                      "&:hover": {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => selecionarRelatorio('fluxo-caixa')}
                  >
                    <Typography variant="h4" color="primary" mb={1}>
                      📈
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" mb={1} textAlign="center">
                      Fluxo de Caixa
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Demonstra a movimentação financeira da empresa, mostrando entradas e saídas de caixa.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid 
                  item 
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '50%',
                      md: '33.33%',
                      lg: '25%'
                    },
                    p: 1
                  }}
                >
                  <Paper 
                    elevation={3}
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      height: "250px",
                      width: "100%",
                      minWidth: "280px",
                      "&:hover": {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => selecionarRelatorio('posicao-categoria')}
                  >
                    <Typography variant="h4" color="primary" mb={1}>
                      📊
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" mb={1} textAlign="center">
                      Posição por Categoria
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Apresenta a situação atual dos recebimentos e pagamentos por categoria.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid 
                  item 
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '50%',
                      md: '33.33%',
                      lg: '25%'
                    },
                    p: 1
                  }}
                >
                  <Paper 
                    elevation={3}
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      height: "250px",
                      width: "100%",
                      minWidth: "280px",
                      "&:hover": {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => selecionarRelatorio('analise-inadimplencia')}
                  >
                    <Typography variant="h4" color="primary" mb={1}>
                      ⚠️
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" mb={1} textAlign="center">
                      Análise de Inadimplência
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Identifica clientes inadimplentes e ajuda a otimizar processos de cobrança.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid 
                  item 
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '50%',
                      md: '33.33%',
                      lg: '25%'
                    },
                    p: 1
                  }}
                >
                  <Paper 
                    elevation={3}
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      height: "250px",
                      width: "100%",
                      minWidth: "280px",
                      "&:hover": {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => selecionarRelatorio('analise-cobranca')}
                  >
                    <Typography variant="h4" color="primary" mb={1}>
                      📊
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" mb={1} textAlign="center">
                      Análise de Desempenho de Cobrança
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Avalia a eficiência das cobranças.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid 
                  item 
                  sx={{
                    width: {
                      xs: '100%',
                      sm: '50%',
                      md: '33.33%',
                      lg: '25%'
                    },
                    p: 1
                  }}
                >
                  <Paper 
                    elevation={3}
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      height: "250px",
                      width: "100%",
                      minWidth: "280px",
                      "&:hover": {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => selecionarRelatorio('analise-vencimentos')}
                  >
                    <Typography variant="h4" color="primary" mb={1}>
                      📅
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" mb={1} textAlign="center">
                      Análise de Vencimentos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Permite planejar pagamentos e recebimentos de forma mais eficiente.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          ) : (
            // Tela de visualização do relatório
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Cabeçalho do relatório */}
              <Box sx={{ 
                p: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {dadosRelatorio.titulo}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setRelatorioSelecionado(null)}
                    >
                      ← Voltar
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={imprimirRelatorio}
                      startIcon={<Print />}
                    >
                      Imprimir
                    </Button>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {dadosRelatorio.descricao}
                </Typography>
              </Box>


              {/* Conteúdo do relatório */}
              <Box sx={{ flex: 1, p: 2, overflow: 'auto' }}>
                {/* Filtros de período específicos para Contas a Pagar */}
                {(dadosRelatorio.titulo === 'Contas a Pagar' || dadosRelatorio.titulo === 'Contas a Receber') && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <DatePicker
                          label="De"
                          value={converterStringParaDate(filtrosRelatorio.dataInicio)}
                          onChange={(newValue) => {
                            const v = converterDateParaString(newValue);
                            setFiltrosRelatorio(prev => ({ ...prev, dataInicio: v }));
                          }}
                          format="dd/MM/yyyy"
                          slotProps={{
                            textField: {
                              size: 'small',
                              helperText: filtrosRelatorio.dataInicio ? formatarDataParaExibicao(filtrosRelatorio.dataInicio) : 'Início do período'
                            }
                          }}
                        />
                        <DatePicker
                          label="Até"
                          value={converterStringParaDate(filtrosRelatorio.dataFim)}
                          onChange={(newValue) => {
                            const v = converterDateParaString(newValue);
                            setFiltrosRelatorio(prev => ({ ...prev, dataFim: v }));
                          }}
                          format="dd/MM/yyyy"
                          slotProps={{
                            textField: {
                              size: 'small',
                              helperText: filtrosRelatorio.dataFim ? formatarDataParaExibicao(filtrosRelatorio.dataFim) : 'Fim do período'
                            }
                          }}
                        />
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <InputLabel>Categoria</InputLabel>
                          <Select
                            value={filtrosRelatorio.categoria}
                            label="Categoria"
                            onChange={(event) => setFiltrosRelatorio(prev => ({
                              ...prev,
                              categoria: event.target.value
                            }))}
                            disabled={loadingCategorias || Boolean(erroCategorias)}
                          >
                            <MenuItem value="">Todas as categorias</MenuItem>
                            {categorias.map(categoria => (
                              <MenuItem
                                key={categoria.categoriaId}
                                value={categoria.st_nomeCategoria}
                              >
                                {categoria.st_nomeCategoria}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button variant="contained" size="small" onClick={() => carregarDadosRelatorio(relatorioSelecionado)}>
                          Filtrar
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          Período padrão: mês corrente
                        </Typography>
                      </Box>
                    </LocalizationProvider>
                  </Paper>
                )}
                {loadingRelatorio ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    {/* Resumo do relatório */}
                    {dadosRelatorio.total !== undefined && (
                      <Paper sx={{ p: 2, mb: 2, backgroundColor: (theme) => theme.palette.success.light, color: 'white' }}>
                        <Typography variant="h6" fontWeight="bold">
                          Total: {formatarMoeda(dadosRelatorio.total)}
                        </Typography>
                      </Paper>
                    )}

                    {/* Gráfico para Fluxo de Caixa */}
                    {dadosRelatorio.titulo === 'Fluxo de Caixa' && dadosRelatorio.dadosGrafico ? (
                      <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" fontWeight="bold" mb={2}>
                          Gráfico dos Últimos 6 Meses
                        </Typography>
                        
                        {/* Resumo dos totais */}
                        {dadosRelatorio.resumo && (
                          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                            <Paper sx={{ p: 2, flex: 1, minWidth: 200, backgroundColor: '#e8f5e8' }}>
                              <Typography variant="body2" color="text.secondary">
                                Entradas realizadas
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="success.main">
                                {formatarMoeda(dadosRelatorio.resumo.entradasRealizadas)}
                              </Typography>
                            </Paper>
                            <Paper sx={{ p: 2, flex: 1, minWidth: 200, backgroundColor: '#fff3e0' }}>
                              <Typography variant="body2" color="text.secondary">
                                Saídas realizadas
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="warning.main">
                                {formatarMoeda(dadosRelatorio.resumo.saidasRealizadas)}
                              </Typography>
                            </Paper>
                            <Paper sx={{ p: 2, flex: 1, minWidth: 200, backgroundColor: '#e3f2fd' }}>
                              <Typography variant="body2" color="text.secondary">
                                Saldo realizado
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color={dadosRelatorio.resumo.saldoRealizado >= 0 ? 'success.main' : 'error.main'}>
                                {formatarMoeda(dadosRelatorio.resumo.saldoRealizado)}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        
                        <Box sx={{ height: 400, width: '100%' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={dadosRelatorio.dadosGrafico}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="mes" />
                              <YAxis />
                              <RechartsTooltip 
                                formatter={(value, name) => [
                                  formatarMoeda(value), 
                                  name === 'receber' ? 'Receber' : 
                                  name === 'pagar' ? 'Pagar' : 'Diferença'
                                ]}
                                labelFormatter={(label) => `Mês: ${label}`}
                              />
                              <Legend />
                              <Bar 
                                dataKey="diferenca" 
                                fill="#8884d8" 
                                name="Diferença (Receber - Pagar)"
                                radius={[4, 4, 0, 0]}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="receber" 
                                stroke="#82ca9d" 
                                strokeWidth={3}
                                name="Receber"
                                dot={{ fill: '#82ca9d', strokeWidth: 2, r: 6 }}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="pagar" 
                                stroke="#ff7300" 
                                strokeWidth={3}
                                name="Pagar"
                                dot={{ fill: '#ff7300', strokeWidth: 2, r: 6 }}
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>
                    ) : null}

                    {/* Gráfico para Posição por Categoria */}
                    {dadosRelatorio.titulo === 'Posição por Categoria' && dadosRelatorio.dadosGrafico ? (
                      <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" fontWeight="bold" mb={2}>
                          Evolução por Categoria - Últimos 6 Meses
                        </Typography>
                        
                        {/* Resumo por categoria */}
                        {Array.isArray(dadosRelatorio.dados) && dadosRelatorio.dados.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                            {dadosRelatorio.dados.map((valores, index) => {
                              const categoria = valores.categoria;
                              const saldo = valores.receber - valores.pagar;
                              return (
                              <Paper 
                                key={`${categoria}-${index}`} 
                                  sx={{ 
                                    p: 2, 
                                    flex: 1, 
                                    minWidth: 200, 
                                    backgroundColor: `hsl(${(index * 60) % 360}, 70%, 95%)`,
                                    border: `2px solid hsl(${(index * 60) % 360}, 70%, 70%)`
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                    {categoria}
                                  </Typography>
                                  <Typography variant="body2" color="success.main" fontWeight="bold">
                                    Receber: {formatarMoeda(valores.receber)}
                                  </Typography>
                                  <Typography variant="body2" color="warning.main" fontWeight="bold">
                                    Pagar: {formatarMoeda(valores.pagar)}
                                  </Typography>
                                  <Typography variant="body2" color={saldo >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                                    Saldo: {formatarMoeda(saldo)}
                                  </Typography>
                                </Paper>
                              );
                            })}
                          </Box>
                        )}
                        
                        <Box sx={{ height: 500, width: '100%' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={dadosRelatorio.dadosGrafico}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="mes" 
                                type="category"
                                allowDuplicatedCategory={false}
                              />
                              <YAxis />
                              <RechartsTooltip 
                                formatter={(value, name) => [
                                  formatarMoeda(value), 
                                  name === 'receber' ? 'Total a Receber' : 
                                  name === 'pagar' ? 'Total a Pagar' : 
                                  name === 'saldo' ? 'Saldo' : name
                                ]}
                                labelFormatter={(label) => `Mês: ${label}`}
                              />
                              <Legend />
                              {(dadosRelatorio.categorias || []).map((categoria, index) => (
                                <Line
                                  key={categoria}
                                  type="monotone"
                                  dataKey={(item) => item.categorias?.[categoria]?.saldo || 0}
                                  stroke={`hsl(${(index * 67) % 360}, 65%, 45%)`}
                                  strokeWidth={3}
                                  name={categoria}
                                  connectNulls
                                />
                              ))}
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>
                        
                        {/* Posição líquida atual */}
                        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Paper sx={{ p: 2, flex: 1, minWidth: 200, backgroundColor: '#e3f2fd' }}>
                            <Typography variant="body2" color="text.secondary">
                              Posição líquida atual
                            </Typography>
                            <Typography 
                              variant="h6" 
                              fontWeight="bold" 
                              color={dadosRelatorio.total >= 0 ? "success.main" : "error.main"}
                            >
                              {formatarMoeda(dadosRelatorio.total)}
                            </Typography>
                          </Paper>
                        </Box>
                      </Paper>
                    ) : null}

                    {/* Gráfico para Análise de Desempenho de Cobrança */}
                    {dadosRelatorio.titulo === 'Análise de Desempenho de Cobrança' && dadosRelatorio.dadosGrafico ? (
                      <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" fontWeight="bold" mb={2}>
                          Desempenho de Cobrança - Últimos 6 Meses
                        </Typography>
                        
                        {/* Resumo da eficiência */}
                        {dadosRelatorio.eficiencia && (
                          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                            <Paper sx={{ p: 2, flex: 1, minWidth: 150, backgroundColor: '#e8f5e8' }}>
                              <Typography variant="body2" color="text.secondary">
                                Valor vencido
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="primary.main">
                                {formatarMoeda(dadosRelatorio.eficiencia.valorVencido)}
                              </Typography>
                            </Paper>
                            <Paper sx={{ p: 2, flex: 1, minWidth: 150, backgroundColor: '#e8f5e8' }}>
                              <Typography variant="body2" color="text.secondary">
                                Valor recebido
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="success.main">
                                {formatarMoeda(dadosRelatorio.eficiencia.valorRecebido)}
                              </Typography>
                            </Paper>
                            <Paper sx={{ p: 2, flex: 1, minWidth: 150, backgroundColor: '#fff3e0' }}>
                              <Typography variant="body2" color="text.secondary">
                                Recuperação
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="warning.main">
                                {Number(dadosRelatorio.eficiencia.percentualRecuperacao || 0).toFixed(1)}%
                              </Typography>
                            </Paper>
                            <Paper sx={{ p: 2, flex: 1, minWidth: 150, backgroundColor: '#ffebee' }}>
                              <Typography variant="body2" color="text.secondary">
                                Ainda vencidas
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" color="error.main">
                                {dadosRelatorio.eficiencia.aindaVencidos}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        
                        <Box sx={{ height: 400, width: '100%' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsBarChart data={dadosRelatorio.dadosGrafico}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="mes" 
                                angle={-45}
                                textAnchor="end"
                                height={80}
                              />
                              <YAxis 
                                tickFormatter={(value) => formatarMoeda(value)}
                                width={100}
                              />
                              <RechartsTooltip 
                                formatter={(value, name) => [
                                  formatarMoeda(value), 
                                  name === 'recebido' ? 'Recebido' : 'Vencido'
                                ]}
                                labelFormatter={(label) => `Mês: ${label}`}
                              />
                              <Legend />
                              <Bar 
                                dataKey="recebido"
                                fill="#4caf50" 
                                name="Recebido"
                                radius={[4, 4, 0, 0]}
                              />
                              <Bar 
                                dataKey="vencido"
                                fill="#ff9800" 
                                name="Vencido"
                                radius={[4, 4, 0, 0]}
                              />
                            </RechartsBarChart>
                          </ResponsiveContainer>
                        </Box>
                        
                        {/* Resumo dos totais */}
                        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Paper sx={{ p: 2, flex: 1, minWidth: 200, backgroundColor: '#e8f5e8' }}>
                            <Typography variant="body2" color="text.secondary">
                              Total recebido
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="success.main">
                              {formatarMoeda(dadosRelatorio.eficiencia.valorRecebido)}
                            </Typography>
                          </Paper>
                          <Paper sx={{ p: 2, flex: 1, minWidth: 200, backgroundColor: '#fff3e0' }}>
                            <Typography variant="body2" color="text.secondary">
                              Saldo não recebido
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="warning.main">
                              {formatarMoeda(dadosRelatorio.eficiencia.saldoNaoRecebido)}
                            </Typography>
                          </Paper>
                        </Box>
                      </Paper>
                    ) : null}

                    {/* Tabela de dados */}
                    {Array.isArray(dadosRelatorio.dados) && dadosRelatorio.dados.length > 0 ? (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              {dadosRelatorio.colunas.map((coluna, index) => {
                                // Mapear nomes das colunas para propriedades de ordenação
                                const getOrderProperty = (coluna) => {
                                  switch(coluna) {
                                    case 'Cliente': return 'nome_pessoa';
                                    case 'Descrição': return 'descricao';
                                    case 'Data Vencimento': return 'data_vencimento';
                                    case 'Dias Atraso': return 'diasAtraso';
                                    case 'Valor': return 'valor_total';
                                    case 'Status': return 'status';
                                    default: return null;
                                  }
                                };
                                
                                const orderProperty = getOrderProperty(coluna);
                                
                                return (
                                  <TableCell key={index} sx={{ fontWeight: 'bold' }}>
                                    {orderProperty ? (
                                      <TableSortLabel
                                        active={orderBy === orderProperty}
                                        direction={orderBy === orderProperty ? order : "asc"}
                                        onClick={() => handleRequestSort(orderProperty)}
                                      >
                                        {coluna}
                                      </TableSortLabel>
                                    ) : (
                                      coluna
                                    )}
                                  </TableCell>
                                );
                              })}
                              {/* Coluna adicional para botão Bloquear apenas no relatório de inadimplência */}
                              {dadosRelatorio.titulo === 'Análise de Inadimplência' && (
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                  Ações
                                </TableCell>
                              )}
                            </TableRow>
                            {dadosRelatorio.titulo === 'Análise de Inadimplência' && (
                              <TableRow>
                                <TableCell colSpan={dadosRelatorio.colunas.length + 1}>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <TextField
                                      size="small"
                                      value={filtrosInadimplencia.cliente}
                                      onChange={(e) => setFiltrosInadimplencia(prev => ({ ...prev, cliente: e.target.value }))}
                                      placeholder="Cliente"
                                    />
                                    <FormControl size="small" sx={{ minWidth: 180 }}>
                                      <Select
                                        value={filtrosInadimplencia.categoria}
                                        displayEmpty
                                        onChange={(e) => setFiltrosInadimplencia(prev => ({ ...prev, categoria: e.target.value }))}
                                      >
                                        <MenuItem value="">Todas as categorias</MenuItem>
                                        {categorias.map(categoria => (
                                          <MenuItem key={categoria.categoriaId} value={categoria.st_nomeCategoria}>
                                            {categoria.st_nomeCategoria}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={filtrosInadimplencia.diasAtrasoMin}
                                      onChange={(e) => setFiltrosInadimplencia(prev => ({ ...prev, diasAtrasoMin: e.target.value }))}
                                      placeholder="Atraso mínimo"
                                    />
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={filtrosInadimplencia.diasAtrasoMax}
                                      onChange={(e) => setFiltrosInadimplencia(prev => ({ ...prev, diasAtrasoMax: e.target.value }))}
                                      placeholder="Atraso máximo"
                                    />
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={filtrosInadimplencia.valorMin}
                                      onChange={(e) => setFiltrosInadimplencia(prev => ({ ...prev, valorMin: e.target.value }))}
                                      placeholder="Saldo mínimo"
                                    />
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={filtrosInadimplencia.valorMax}
                                      onChange={(e) => setFiltrosInadimplencia(prev => ({ ...prev, valorMax: e.target.value }))}
                                      placeholder="Saldo máximo"
                                    />
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableHead>
                          <TableBody>
                            {dadosRelatorio.dados.map((item, index) => {
                              // Calcular dias de atraso para inadimplência
                              const dataVencimentoRelatorio = parseDataLocal(item.data_vencimento);
                              const diasAtraso = dataVencimentoRelatorio ?
                                Math.floor((new Date() - dataVencimentoRelatorio) / (1000 * 60 * 60 * 24)) : 0;
                              
                              // Calcular dias restantes para vencimentos
                              const diasRestantes = dataVencimentoRelatorio ?
                                Math.ceil((dataVencimentoRelatorio - new Date()) / (1000 * 60 * 60 * 24)) : 0;
                              
                              return (
                                <TableRow key={index}>
                                  {/* Renderizar células baseado nas colunas do relatório */}
                                  {dadosRelatorio.colunas.map((coluna, colIndex) => {
                                    switch(coluna) {
                                      case 'Cliente':
                                        return (
                                          <TableCell key={colIndex}>
                                            {item.nome_pessoa || 'N/A'}
                                          </TableCell>
                                        );
                                      case 'Descrição':
                                        return (
                                          <TableCell key={colIndex}>
                                            {item.descricao || 'N/A'}
                                          </TableCell>
                                        );
                                      case 'Categoria':
                                        return (
                                          <TableCell key={colIndex}>
                                            {item.categoria || 'N/A'}
                                          </TableCell>
                                        );
                                      case 'Tipo':
                                        return (
                                          <TableCell key={colIndex}>
                                            <Chip 
                                              label={item.tipo === 'receber' ? 'Receber' : 'Pagar'}
                                              color={item.tipo === 'receber' ? 'success' : 'error'}
                                              size="small"
                                            />
                                          </TableCell>
                                        );
                                      case 'Fornecedor':
                                        return (
                                          <TableCell key={colIndex}>
                                            {item.nome_pessoa || 'N/A'}
                                          </TableCell>
                                        );
                                      case 'Data Vencimento':
                                        return (
                                          <TableCell key={colIndex}>
                                            {dataVencimentoRelatorio ? dataVencimentoRelatorio.toLocaleDateString('pt-BR') : 'N/A'}
                                          </TableCell>
                                        );
                                      case 'Data Pagamento':
                                        return (
                                          <TableCell key={colIndex}>
                                            {item.data_pagamento ? formatarData(item.data_pagamento) : 'N/A'}
                                          </TableCell>
                                        );
                                      case 'Dias Atraso':
                                        return (
                                          <TableCell key={colIndex}>
                                            <Chip 
                                              label={`${item.dias_atraso ?? diasAtraso} dias`}
                                              color={(item.dias_atraso ?? diasAtraso) > 30 ? 'error' : (item.dias_atraso ?? diasAtraso) > 15 ? 'warning' : 'info'}
                                              size="small"
                                            />
                                          </TableCell>
                                        );
                                      case 'Dias Restantes':
                                        return (
                                          <TableCell key={colIndex}>
                                            <Chip 
                                              label={`${item.dias_restantes ?? diasRestantes} dias`}
                                              color={(item.dias_restantes ?? diasRestantes) < 0 ? 'error' : (item.dias_restantes ?? diasRestantes) <= 3 ? 'warning' : 'success'}
                                              size="small"
                                            />
                                          </TableCell>
                                        );
                                      case 'Status':
                                        return (
                                          <TableCell key={colIndex}>
                                            <Chip 
                                              label={item.status || 'N/A'}
                                              color={item.status === 'pago' ? 'success' : item.status === 'pendente' ? 'warning' : 'default'}
                                              size="small"
                                            />
                                          </TableCell>
                                        );
                                      case 'Valor':
                                      case 'Saldo em Aberto':
                                        return (
                                          <TableCell key={colIndex} sx={{ fontWeight: 'bold' }}>
                                            {formatarMoeda(item.saldo_aberto ?? item.valor_realizado ?? item.valor_total ?? 0)}
                                          </TableCell>
                                        );
                                      case 'Valor Original':
                                        return (
                                          <TableCell key={colIndex}>
                                            {formatarMoeda(item.valor_original ?? item.valor_total ?? 0)}
                                          </TableCell>
                                        );
                                      case 'Valor Realizado':
                                        return (
                                          <TableCell key={colIndex}>
                                            {formatarMoeda(item.valor_realizado ?? 0)}
                                          </TableCell>
                                        );
                                      case 'A Receber':
                                        return <TableCell key={colIndex}>{formatarMoeda(item.receber || 0)}</TableCell>;
                                      case 'A Pagar':
                                        return <TableCell key={colIndex}>{formatarMoeda(item.pagar || 0)}</TableCell>;
                                      case 'Saldo':
                                        return <TableCell key={colIndex}>{formatarMoeda(item.saldo || 0)}</TableCell>;
                                      default:
                                        return (
                                          <TableCell key={colIndex}>
                                            N/A
                                          </TableCell>
                                        );
                                    }
                                  })}
                                  {/* Botão Bloquear apenas no relatório de inadimplência */}
                                  {dadosRelatorio.titulo === 'Análise de Inadimplência' && (
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <Button
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        startIcon={<Block />}
                                        onClick={() => bloquearCliente(item)}
                                        sx={{ 
                                          minWidth: 'auto',
                                          px: 1,
                                          py: 0.5,
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        Bloquear
                                      </Button>
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Paper sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          Nenhum dado encontrado para este relatório.
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbars para mensagens */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </AcessoRestrito>
  );
}
